/**
 * Admin UI bilingual strings (en / zh).
 * Used via RegionContext → useRegion().t
 */

export const adminI18n = {
  en: {
    // Tabs
    tabIntro: 'Introduction',
    tabMedia: '1. Dataset',
    tabBuilder: '2. Builder',
    tabHost: '3. Host',
    tabShare: '4. Share',
    tabResults: '5. Results',
    tabPractice: 'Practice',
    tabSilicon: 'Silicon',
    siliconPageTitle: 'Silicon pretest',

    // Toolbar
    toggleSidebar: 'Toggle Project Sidebar',
    saveTooltipDirty: 'Save draft changes',
    saveTooltip: 'Save',
    aiTooltip: 'Open the AI Assistant sidebar — models and MCP stay in Settings',
    aiLabel: 'AI',
    toggleAiSidebarOpen: 'Close AI Assistant',
    toggleAiSidebarClosed: 'Open AI Assistant',
    aiSidebarTitle: 'AI Assistant',
    aiSidebarSubtitle: 'Chat to design this project',
    aiSidebarSelectProject: 'Select a project first to send messages.',
    aiSidebarNoProject: 'Select a project to start chatting.',
    aiSidebarNotConnected: 'Not connected — open Settings to add an API key.',
    aiSidebarModelUnavailable: 'Select a configured model before sending. The previous provider may have been removed.',
    aiSidebarSettings: 'Assistant settings',
    aiSidebarSilicon: 'Silicon pretest',
    aiSidebarClear: 'Clear history',
    aiSidebarDownload: 'Download conversation',
    aiSidebarClose: 'Close AI sidebar',
    aiSidebarMore: 'More actions',
    aiSidebarEmptyTitle: 'Ask the Assistant to design this survey',
    aiSidebarEmptyReady: 'AI Assistant is ready. Describe the survey or a change you want.',
    aiSidebarEmptyConnect: 'Open Settings to connect an API key, or use Codex / MCP from Integrations.',
    aiSidebarComposerPlaceholder: "Type a message… (e.g. 'Create a thermal comfort survey')",
    aiSidebarComposerDisabled: 'Select a project and configure a model first.',
    aiSidebarUndo: 'Undo last AI change',
    aiSidebarConnected: 'Connected',
    aiSidebarDisconnected: 'Not connected',
    aiSidebarOpenIntegrations: 'Manage models & MCP',
    aiSidebarModel: 'Model',
    aiSidebarReasoning: 'Reasoning',
    aiSidebarMode: 'Assistant mode',
    aiSidebarModeAgent: 'Agent',
    aiSidebarModeGenerate: 'Generate',
    aiSidebarModeAdjust: 'Adjust',
    aiSidebarModeQuestion: 'Question',
    aiSidebarSelectModel: 'Select model',
    aiSidebarConfigureModel: 'Configure a model first',
    aiSidebarAssistantName: 'Assistant',
    aiSidebarSend: 'Send message',
    aiSidebarStop: 'Stop current run',
    aiSidebarApprovalPrompt: 'This action requires approval',
    aiSidebarApprovalRisk: 'Risk: {risk}',
    aiSidebarApprovalProject: 'Project: {project}',
    aiSidebarApprovalApprove: 'Approve',
    aiSidebarApprovalDeny: 'Deny',
    aiSidebarSteer: 'Steer',
    aiSidebarSteerPlaceholder: 'Add an instruction to the running Agent…',
    aiSidebarStatusThinking: 'Thinking…',
    aiSidebarStatusQueued: 'Queued…',
    aiSidebarStatusRunning: 'Continuing survey generation…',
    aiSidebarStatusApproval: 'Waiting for your approval…',
    aiSidebarStatusRetrying: 'Retrying model request…',
    aiSidebarStatusCompacting: 'Compacting context and continuing…',
    aiSidebarStatusVerifying: 'Verifying saved draft…',
    aiSidebarStatusUsingTool: 'Using {tool}…',
    aiSidebarStatusStep: 'Working on step {step}…',
    aiSidebarElapsed: '{seconds}s',
    aiSidebarToolRunning: 'running',
    aiSidebarToolDone: 'done',
    aiSidebarToolError: 'failed',
    aiSidebarToolUnknown: 'unknown',
    aiSidebarMemoryHint: 'Memory is project-specific •',
    aiSidebarUnnamedProject: 'Unnamed project',
    aiSidebarCodexTitle: 'Codex / MCP',
    aiSidebarCodexBody: 'Advanced path for Cursor, Claude, and ChatGPT via MCP.',
    aiSettingsTitle: 'Assistant settings',
    aiSettingsModels: 'Models',
    aiSettingsContext: 'Context',
    aiSettingsAdvanced: 'Advanced',
    aiSettingsLocalKeyIntro: 'Connect an OpenAI or OpenRouter API key for this browser.',
    aiSettingsValidate: 'Validate',
    aiSettingsContextTitle: 'Study context',
    aiSettingsContextIntro: 'Optional details that help the Assistant stay aligned with this project.',
    aiSettingsMemory: 'Project memory',
    aiSettingsMemoryHint: 'Keep conversation context and learned preferences within this project.',
    aiSettingsMemoryAutomatic: 'Conversation continuity is handled automatically by the hosted Agent session for this project.',
    aiSettingsResearchTopic: 'Research topic',
    aiSettingsResearchRequirements: 'Research requirements',
    aiSettingsScenario: 'Survey scenario',
    aiSettingsCustomScenario: 'Add a custom scenario',
    aiSettingsAdd: 'Add',
    aiSettingsAdvancedTitle: 'Advanced',
    aiSettingsAdvancedIntro: 'Experimental and maintenance controls. Most studies do not need these.',
    aiSettingsMultiAgent: 'Multi-agent review',
    aiSettingsExperimental: 'Experimental',
    aiSettingsMultiAgentUnavailable: 'The current hosted Agent Runtime does not support the legacy multi-agent review stream. The feature is retained for self-hosted development, but is kept out of the everyday workflow here.',
    aiSettingsMultiAgentToggle: 'Review generated surveys with multiple specialist prompts',
    aiSettingsIndependentReview: 'Independent reviews',
    aiSettingsGroupReview: 'Group discussion',
    aiSettingsReviewRounds: 'Maximum review rounds',
    aiSettingsEditAgents: 'Edit specialist prompts',
    aiSettingsPrompts: 'System prompts',
    aiSettingsPromptsHint: 'Legacy local assistant only',
    aiSettingsPromptGenerate: 'Generate survey prompt',
    aiSettingsPromptAdjust: 'Adjust survey prompt',
    aiSettingsPromptIntent: 'Intent detection prompt',
    aiSettingsPromptQuestion: 'Question answering prompt',
    aiSettingsReset: 'Reset',
    aiSettingsSave: 'Save',
    aiSettingsConversationData: 'Conversation data',
    aiSettingsConversationDataHint: 'Download this conversation or clear it from the current project.',
    aiClearHistoryTitle: 'Clear conversation',
    aiClearHistoryMessage: 'Clear conversation history for this project?',
    aiClearHistoryConfirm: 'Clear',
    previewSurvey: 'Preview Survey',
    viewLive: 'Survey Link',
    moreTools: 'More workspace tools',
    signedInAs: 'Signed in as',
    changeTheme: 'Change theme',
    skillsLibrary: 'Custom interactions',
    adminDashboard: 'Admin Dashboard',
    templatesAndProjects: 'Templates and projects',
    clearEditingState: 'Clear editing state',
    signOut: 'Sign out',
    chooseTheme: 'Choose Theme',
    loadingProjectSystem: 'Loading project system...',
    loadingWorkspace: 'Loading workspace…',
    noProjectTitle: 'No Project Selected',
    noProjectBody: 'Create a new project or select an existing one from the sidebar to get started.',
    openProjectSidebar: 'Open Project Sidebar',
    saveStatusSaving: 'Saving…',
    saveStatusError: 'Save failed — click Save to retry',
    saveStatusUnsaved: 'Unsaved changes',
    saveStatusJustNow: 'Auto-saved just now',
    saveStatusSecsAgo: 'Auto-saved {n}s ago',
    saveStatusMinsAgo: 'Auto-saved {n}m ago',
    saveStatusAllSaved: 'All changes saved',

    // Introduction
    introTitle: 'How this Admin workspace works',
    introBody:
      'Design with the AI Assistant sidebar first (your own API key). Codex / Cursor MCP remains an optional advanced path.',
    introStep1Title: 'Step 1 — Media Dataset',
    introStep1Blurb: 'Upload and organize images, video, and audio; tag folders for set/category modes.',
    introStep2Title: 'Step 2 — Survey Builder',
    introStep2Blurb: 'Design pages and questions; saves update the draft. Released projects stay unchanged until the next release.',
    introStep3Title: 'Step 3 — Host',
    introStep3Blurb: 'Set up Supabase, release the participant snapshot, then deploy the participant site and save its URL.',
    introStep4Title: 'Step 4 — Share Survey',
    introStep4Blurb: 'Copy the survey link, download the QR code, and run a pre-launch test before inviting participants.',
    introStep5Title: 'Step 5 — Results Analysis',
    introStep5Blurb: 'Browse responses, export data, and run analyses for this project.',
    introAddonTitle: 'Add-on — Researcher Practice',
    introAddonBlurb: 'Take the survey yourself to check flow and media before inviting participants.',
    introFlowTitle: 'Recommended: in-browser AI Assistant',
    introFlowLead:
      'Store a DeepSeek / OpenRouter / OpenAI key, then talk to the Assistant from the right sidebar on any Admin tab. MCP (Codex, Cursor, Claude) is optional for people who already live in those tools.',
    introFlow1Title: 'Save an API key and choose a model',
    introFlow1Body:
      'Open AI & Integrations, add a supported provider key, then choose default models for the Assistant and Silicon pretests.',
    introFlow2Title: 'Ask the Assistant to build the survey',
    introFlow2Body:
      'Open the AI sidebar and describe the topic and study design. The Assistant edits the current project draft with conflict-safe operations.',
    introFlow3Title: 'Add media if you skipped it',
    introFlow3Body:
      'For images/video/audio, upload files or import from Hugging Face on the Media tab. Do not generate synthetic media for upload. For set/category modes, tag folders as set or category.',
    introFlow4Title: 'Host, then share',
    introFlow4Body:
      'On Host, set up Supabase, release a participant snapshot, and deploy the participant site. Then copy the link on Share. Legacy projects remain live-on-save only until their first release.',
    introFlow5Title: 'Analyze results',
    introFlow5Body:
      'After data comes in, ask Codex to analyze a specific project. It can summarize, export JSON/CSV, and help interpret — or you can open Step 5 Results Analysis in Admin.',
    connectCodex: 'Set up AI Assistant',
    openSurveyBuilder: 'Open Survey Builder',
    openMediaDataset: 'Open Media Dataset',
    openHost: 'Open Host',
    openShareSurvey: 'Open Share Survey',
    openResultsAnalysis: 'Open Results Analysis',
    nextHost: 'Next: Host →',

    // Media Dataset
    mediaTitle: 'Media Dataset',
    mediaDescription:
      'Add media, organize it in folders, then tag folders as set or category for survey assignment. Files are stored in your Supabase Storage bucket.',
    mediaAddSection: '1 · Add media',
    mediaOrganizeSection: '2 · Organize in folders',
    mediaTaggedSection: '3 · Tagged for assignment',
    mediaUploadTitle: 'Upload Media',
    mediaUploadHelpPrefix: 'Upload into the current folder in Media library',
    mediaUploadLimitsHelp:
      'Each file: max {mb} MB. Image compression is optional; video, audio and animated GIFs retain their original data.',
    mediaAvTooLarge: '"{name}" is too large ({size} MB). Video/audio max is {mb} MB.',
    mediaChooseFiles: 'Choose files',
    mediaSelected: 'selected',
    mediaUploadBtn: 'Upload',
    mediaUploadRoot: '→ root',
    mediaNoFoldersTagged:
      'No folders tagged yet. In Media library, check folders on the left and click Set or Category. Tagged folders will appear here for survey assignment.',
    mediaTaggedSets: 'Tagged sets ({n})',
    mediaTaggedSetsHelp:
      'Folders tagged set. Each folder’s direct files stay together when a question uses “Random fixed sets” with a matching media count.',
    mediaTaggedCategories: 'Tagged categories ({n})',
    mediaTaggedCategoriesHelp:
      'Folders tagged category. Questions can draw random files from each category.',
    mediaCheckingR2: 'Checking storage for existing images…',
    mediaLastUpload: 'Last upload:',
    hfImportTitle: 'HF Dataset Import',
    hfImportHelp:
      'Batch-import from HuggingFace. Use owner/dataset (rows) or owner/dataset/folder (recursive; nested folders kept). Resume reuses the first import destination to avoid nested duplicates.',
    hfEnable: 'Enable HF import',
    hfToken: 'Token (optional)',
    hfDataset: 'Dataset',
    hfPlaceholder: 'owner/dataset',
    saveConfig: 'Save',
    testConnection: 'Test',
    hfPreload: 'Preload to storage',
    hfRePreload: 'Re-preload to storage',
    noMediaYet: 'No media uploaded yet',
    mediaInR2: 'media file(s) in storage',

    // Media guides
    guideFixedSets: 'Fixed sets',
    guideFixedSetsBody:
      'Tag folders as set. Each tagged folder’s direct files are shown together (set size must match the question’s media count).',
    guideExample: 'Example',
    guideFixedSetsExample:
      '1. Create folders block01, block02, block03\n2. Put 2 images in each folder (same count)\n3. Check those folders → click Set\n4. In Survey Builder, choose media mode Random fixed sets with count = 2\n→ each respondent sees one whole folder (both images together)',
    guideTaggedSets: 'Currently tagged sets: {n}',
    guideCategories: 'Categories',
    guideCategoriesBody:
      'Tag folders as category. In Survey Builder, choose Per category and set Files per category. Each tagged folder contributes that many random file(s) (drawn recursively).',
    guideCategoriesExample:
      '1. Create folders street and park\n2. Put several images in each (subfolders OK)\n3. Check those folders → click Category\n4. In the question: Media Assignment → Per category, Files per category = 2\n→ each respondent sees 2 street + 2 park images (4 total)',
    guideTaggedCategories: 'Currently tagged categories: {n}',
    guideProjectFiles: ' · project files: {n}',

    // Survey Builder (ATF)
    builderTitle: 'Survey Builder',
    builderDescription:
      'Build your survey with pages, questions, and theme. Use the AI assistant below to draft and refine questions, then preview the result before sharing.',
    builderSurveySettings: 'Survey Settings',
    builderBasicInfo: '📝 Basic Information',
    builderSurveyTitle: 'Survey Title',
    builderSurveyTitleHelp: 'The main title that appears at the top of your survey',
    builderSurveyDescription: 'Survey Description',
    builderSurveyDescriptionHelp: 'A brief description explaining the purpose of your survey',
    builderCompletionMessage: 'Completion Message (optional)',
    builderCompletionMessageHelp: 'Shown to participants after they submit the survey',
    builderResponseQuota: 'Response Quota (optional)',
    builderResponseQuotaHelp: 'Close the survey automatically after this many responses (leave empty for unlimited)',
    builderLogoSettings: '🖼️ Logo Settings',
    builderDisplaySettings: '⚙️ Display Settings',
    builderSurveyLanguage: 'Survey language',
    builderSurveyLanguageHelp: 'Participant chrome (progress, trials, Next / Complete). Default is English.',
    builderSurveyLanguageEn: 'English',
    builderSurveyLanguageZh: '中文',
    builderThemeCustomization: '🎨 Theme Customization',
    builderPagesQuestions: 'Pages & Questions',
    builderPagesHelp: 'Organize your survey into pages. Drag pages to reorder them.',
    builderAddPage: 'Add New Page',

    hostTitle: 'Host',
    hostDescription:
      'Set up Supabase, release the participant snapshot, then deploy the participant site and save its URL.',
    hostBackendTitle: 'Backend / Supabase',
    hostStepperTitle: 'Deploy the participant site',
    hostStepperHelp:
      'Prepare a deployment folder, test the build, push to GitHub, set Vercel environment variables, then run the pre-launch checklist.',
    hostDeployedUrlLabel: 'Deployed participant URL',
    hostDeployedUrlHelp:
      'Paste the public Vercel (or other) URL after you deploy. Share uses this for the survey link and QR code.',
    hostDeployedUrlPlaceholder: 'https://your-project.vercel.app',
    hostDeployedUrlSave: 'Save URL',
    hostDeployedUrlSaved: 'Saved',
    serverTitle: 'Backend / Supabase',
    serverDescription: 'Set up your Supabase database table to store survey responses.',

    // Share
    shareTitle: 'Share Your Survey',
    shareDescription:
      'Copy the survey link, download the QR code, and run a pre-launch test before inviting participants.',
    shareGoToHost: 'Open Host',
    shareDeployedHint: 'This is the deployed participant URL saved on the Host tab.',
    shareYourLink: 'Your Survey Link',
    shareCopied: 'Copied!',
    shareCopyLink: 'Copy Link',
    shareOpenSurvey: 'Open Survey',
    shareQrAlt: 'Survey QR code',
    shareQrDownload: 'Download QR code (PNG)',
    shareQrHint: 'Scan to open this survey. Download a 1024 × 1024 PNG for sharing or printing.',
    shareQrLoading: 'Generating QR code',
    shareQrError: 'Could not generate the QR code.',
    shareQrRetry: 'Try again',
    shareQrLocalHint: 'This link uses localhost and cannot be opened from another phone. Open the Host tab to deploy the participant site and save its URL, then return here for the shareable link and QR code.',
    shareNoProject: 'No project selected. Please select or create a project first.',
    shareTips: '💡 Sharing Tips',
    shareTip1Primary: 'Share the link directly',
    shareTip1Secondary: 'Send it via email, WeChat, Slack, or any other channel',
    shareTip2Primary: 'Embed in a QR code',
    shareTip2Secondary: 'Download the QR code from Your Survey Link above for invitations, slides or printed materials',
    shareTip3Primary: 'One link for your study',
    shareTip3Secondary: 'With version management enabled, publish a release to update this link. Saving only updates your draft.',
    shareTip4Primary: 'Responses are saved automatically',
    shareTip4Secondary: 'Results go to Supabase in real time and can be viewed in the Results Analysis tab',
    shareAdminLink: '🔒 Admin Panel Link',
    shareAdminHelp: 'Share this link with collaborators who need to edit the survey. They will need to sign in.',
    shareCopyAdmin: 'Copy Admin Link',

    // Results
    resultsScopeHint: "{shown} / {total} submissions in analysis and export. CSV: one row per submission. ZIP: includes per-trial long tables and summaries. Question search only changes visible cards.",
    resultsBrowserPrefs: "Filters are saved in this browser; they do not modify the survey.",
    resultsResetFilters: "Reset filters",
    resultsFilteredEmpty: "No submissions match these filters. Reset filters to see all responses.",
    resultsPreparingExport: "Preparing export…",
    resultsViewResponse: "View",
    resultsSubmissionDetail: "Submission",
    resultsHistoricalRevision: "Not recorded (historical response)",
    resultsGroupExplanation: "One answer evaluates the entire displayed media group. Each ordered group is counted once.",
    resultsCounts: "{answered} / {total} submissions answered ({rate}%) · {people} participants",
    resultsRecordTrial: "Trial",
    resultsNoMedia: "No media",
    resultsClose: "Close",
    resultsRevision: "Revision",
    resultsQuality: "Quality",
    resultsDetailSession: "Session",
    resultsMixedRevisions: "These responses span multiple questionnaire revisions. Analysis and export use the selected version only to avoid mixing incompatible settings. Switch versions below; historical responses without a revision remain unknown.",
    resultsAllRevisions: "All revisions",
    resultsRowsPerPage: "Rows per page",
    resultsTitle: 'Results Analysis',
    resultsDescriptionPrefix: 'Analyze survey responses per question for project:',
    resultsRefresh: 'Refresh responses',
    resultsExportCsv: 'Export CSV',
    resultsExportAll: 'Export All',
    resultsExportMethods: 'Export Methods',
    resultsViewOverview: 'Overview',
    resultsViewQuestions: 'Questions',
    resultsViewData: 'Data',
    resultsAnalyze: 'Analyze current results',
    resultsExplainQuestion: 'Explain this question',
    resultsFilters: 'Filters',
    resultsExportMenu: 'Export',
    resultsExportReport: 'This analysis report',
    resultsExportSummary: 'Summary statistics',
    resultsExportRaw: 'Raw / trial data',
    resultsExportBundle: 'Full analysis pack',
    resultsParticipants: 'Participants',
    resultsTrials: 'Answer rounds',
    resultsLatestReport: 'Latest analysis',
    resultsNoReport: 'No analysis report yet. Analyze current results to generate one.',
    resultsReportStaleScope: 'This report used a previous range. Analyze again to update.',
    resultsReportStaleData: 'New responses arrived after this report. Analyze again to update.',
    resultsViewEvidence: 'View evidence',
    resultsDeepPerception: 'Annotation × Perception deep dive',
    resultsOpenPerception: 'Open deep analysis',
    resultsDataSource: 'Data source',
    resultsSourceHuman: 'Human responses',
    resultsSourcePractice: 'Researcher practice only',
    resultsSourceSilicon: 'Silicon run',
    resultsSiliconRun: 'Silicon run ID',
    resultsApplyFilters: 'Apply',
    resultsScopeNow: 'Current range',
    resultsSelectQuestion: 'Select a question',
    resultsQuestionSearch: 'Search questions',
    resultsDateTimezone: 'Dates use {tz}. Records without a valid timestamp are excluded when a date filter is active.',
    resultsQualityIsInfo: 'Quality flags are checks, not deletions.',
    resultsContractFallback: 'Some historical responses have no frozen question contract. Current settings are a fallback.',
    resultsFormalHuman: 'Formal analysis uses human responses unless you choose practice or a Silicon run.',
    resultsConnectedSupabase: 'Connected to Supabase',
    resultsLocalFiles: 'Reading local response files',
    resultsDataQuality: 'Data Quality',
    resultsClean: '{n} clean',
    resultsFlagged: '{n} flagged',
    resultsInAnalysis: '{n} in analysis',
    resultsExcludeFlagged: 'Exclude flagged responses from analysis',
    resultsTotalResponses: 'Total Responses',
    resultsAnswerableQuestions: 'Answerable Questions',
    resultsWithResponses: 'with responses',
    resultsQuestionCoverage: 'Question Coverage',
    resultsCoverageHelp: 'answerable questions with ≥1 response',
    resultsResponseRecords: 'Response records',
    resultsSubmissions: '{n} submissions',
    resultsRecordsHelp: 'Remove individual submissions from this project. Deletion is permanent.',
    resultsRecordsViewHelp: 'View answers, trial details and quality flags for each submission.',
    resultsEmpty: 'No responses collected yet. Share your survey link and come back here to view results.',
    resultsSkillCustomHelp:
      'Custom skill results reuse native charts by declared type: annotation overlays (points/path/polygon/bbox), media ranking (number/boolean with imageUrl), or text/ranking/allocation charts.',
    resultsSkillByMode: 'View by task mode',
    resultsSkillByModeHelp:
      'This skill stores multiple tasks in one question, distinguished by mode (e.g. cue ranking / route trace).',
    resultsSkillFieldStats: 'Field statistics',
    resultsSkillResponseList: 'Responses',
    resultsSkillExampleFirst: 'Example (first response)',
    resultsSkillReadableSummary: 'Readable summary',
    resultsSkillHideAllRaw: 'Hide all raw responses',
    resultsSkillShowAllRaw: 'View all raw responses',
    resultsSkillHideRawJson: 'Hide raw JSON',
    resultsSkillShowRawJson: 'Show raw JSON',
    resultsSkillReadableHint: 'One readable summary per person; expand raw JSON when needed.',
    resultsSkillRawHint: 'Answer fields only — shown media is listed separately per response.',
    resultsSkillResponseN: 'Response {n}',
    resultsSkillStimulusMedia: 'Stimulus media:',
    resultsSkillShowLess: 'Show less',
    resultsSkillShowAllN: 'Show all {n}',
    resultsSkillNoResponses: 'No responses yet.',
    resultsSkillHideRaw: 'Hide raw responses',
    resultsSkillViewRaw: 'View raw responses',
    skillAnswerSubmittedTitle: 'Submitted answer',
    skillAnswerYourSubmittedTitle: 'Your submitted answer',
    skillAnswerRecorded: 'Recorded',
    skillAnswerEmpty: '(no answer)',
    skillAnswerNotRecorded:
      'No answer was recorded for this question (submit button did not save, or it was skipped).',

    // Practice
    practiceTitle: 'Researcher Practice',
    practiceDescription:
      'Practice survey questions, verify media assignment, and inspect results before inviting participants.',
    practiceQuestions: 'Questions',
    practiceStartSession: 'Start session',
    practiceSessionLocked: 'Session locked — answer the queue, or stop the session to free-pick again.',
    practiceFreePick: 'Click any question to answer freely. Result analysis for the selected question is below (collapsed).',
    practiceChooseQuestion: 'Choose a question on the left to practice.',
    practiceTotal: 'Practice total: {n}',
    practiceNewRound: 'New round',
    practiceEndSession: 'End session',
    practiceEditSettings: 'Question settings',
    practiceSettingsSaved: 'Question settings updated. Save the project to persist.',
    practiceSettingsUnavailable: 'Cannot edit question settings without a survey configuration.',

    // Sidebar
    sidebarProjects: 'Projects',
    sidebarTemplates: 'Project Templates',
    sidebarSearchTemplates: 'Search templates...',
    sidebarAllCategories: 'All Categories',
    sidebarCatAcademic: 'Academic Research',
    sidebarCatUrban: 'Urban Theory',
    sidebarCatAi: 'AI Template',
    sidebarSortNameAsc: 'Name A–Z',
    sidebarSortNameDesc: 'Name Z–A',
    sidebarSortNewest: 'Newest',
    sidebarSortOldest: 'Oldest',
    sidebarPinned: 'Pinned',
    sidebarUnknownAuthor: 'Unknown',
    sidebarShowMetadata: 'Show metadata',
    sidebarHideMetadata: 'Hide metadata',
    sidebarPreviewTemplate: 'Preview template',
    sidebarCopyTemplate: 'Copy template',
    sidebarNoDescription: 'No description',
    sidebarMyProjects: 'My Projects ({n})',
    sidebarImportProject: 'Import Project',
    sidebarCreateProject: 'Create New Project',
    sidebarNoProjects: 'No projects yet. Create your first project!',
    sidebarNoTemplates: 'No templates found. Create one from a project.',
    sidebarNoTemplateMatch: 'No templates match your search.',
    sidebarProjectName: 'Project Name',
    sidebarDescriptionOptional: 'Description (Optional)',
    sidebarCancel: 'Cancel',
    sidebarCreate: 'Create',
    sidebarEditProject: 'Edit Project',
    sidebarDuplicate: 'Duplicate',
    sidebarExportProject: 'Export Project',
    sidebarExportIde: 'Export for AI / IDE',
    sidebarExportIdeHelp: 'Credentials removed',
    sidebarSaveAsTemplate: 'Save as Template',
    sidebarPublishMain: 'Publish to Main Page',
    sidebarDelete: 'Delete',

    // Integrations
    integBackAdmin: 'Back to Admin',
    integTitle: 'AI & Integrations',
    integBody:
      'Use the in-browser Assistant with your own API key (DeepSeek recommended). MCP for Codex / Claude / Cursor is an advanced option.',
    integConnectTitle: 'Advanced — Connect ChatGPT (Codex)',
    integAdvanced: 'Advanced',
    integAssistantFirstBody:
      'Enter your API keys to use models from the following providers. Keys are write-only after save, same as DeepSeek Harness.',
    integAssistantModel: 'Assistant model',
    integSiliconModel: 'Silicon VLM',
    integRecommended: 'Recommended',
    integConnected: 'Connected',
    integConnectBody:
      'Use ChatGPT (Codex) to create and edit surveys. Saves update the share link immediately. No OpenAI API key is needed on this website.',
    integMcpEndpoint: 'MCP endpoint (same URL for Codex / Claude Code / Cursor):',
    integLocalMcpHint: 'Local server MCP (dev only)',
    integStep1Title: 'Set ChatGPT (Codex) permission to "Approve for me"',
    integStep1Body:
      'In the ChatGPT (Codex) Work composer, click the permission control (bottom-left of the input) and choose Approve for me. This lets ChatGPT (Codex) edit ~/.codex/config.toml, run codex mcp login, open your browser, and save credentials.',
    integStep2Title: 'Paste this into ChatGPT (Codex)',
    integStep2Body: 'Copy into a new ChatGPT (Codex) chat:',
    integStep3Title: 'Start a new chat, then design a survey',
    integStep3Body: 'Open a new ChatGPT (Codex) chat, then paste:',
    integClaudeTitle: '2. Connect Claude Code',
    integClaudeBody:
      'Use Claude Code in the terminal with the same sp_survey MCP. Saves update the share link immediately. Approve OAuth once in your browser.',
    integClaudeStep1Title: 'Add the MCP server',
    integClaudeStep1Body:
      'In a terminal (or paste the prompt below into Claude Code), add the remote HTTP MCP. Prefer user scope so it works across projects:',
    integOrPastePrompt: 'Or paste this setup prompt into the agent:',
    integClaudeStep2Title: 'Authenticate in Claude Code',
    integClaudeStep2Body:
      'Run /mcp, select sp_survey, then Authenticate. Open the authorize URL in your system browser and Approve on this site. Restart Claude Code if tools do not appear.',
    integClaudeStep3Title: 'Design a survey',
    integClaudeStep3Body: 'In a new Claude Code session, paste:',
    integCursorTitle: '3. Connect Cursor',
    integCursorBody:
      'Use Cursor Agent with the same sp_survey MCP. Add the server JSON, then Connect / Authenticate under Settings → Tools & MCP.',
    integCursorStep1Title: 'Add ~/.cursor/mcp.json (or project .cursor/mcp.json)',
    integCursorStep1Body:
      'Merge into existing mcpServers — do not delete other servers. Cursor uses url only (no type field):',
    integCursorStep2Title: 'Authenticate in Cursor',
    integCursorStep2Body:
      'Open Cursor Settings → Tools & MCP. Find sp_survey and click Connect / Authenticate. Approve in the browser on this site. Reload the window if tools stay grey.',
    integCursorStep3Title: 'Design a survey',
    integCursorStep3Body: 'In a new Cursor Agent chat, paste:',
    integAccounts: 'Connected MCP accounts',
    integNoneYet: 'None yet. Complete a Codex, Claude Code, or Cursor connection above.',
    integLastUsed: 'Last used:',
    integNever: 'Never',
    integDisconnect: 'Disconnect',
    integAssistantTitle: '1. In-browser AI Assistant',
    integReady: 'Ready',
    integAssistantBody:
      'Prefer to stay in the browser? Add your OpenAI or OpenRouter API key, then use the assistant inside Survey Builder.',
    integGetKey: 'Get a key from DeepSeek, OpenRouter, or OpenAI.',
    integApiKeyLabel: 'API key',
    integSaveKey: 'Save key',
    integRemoveKey: 'Remove key',
    integAfterSave: 'After saving, return to Admin, open a project, and expand the AI Assistant panel.',
    integCopy: 'Copy',
    modelsTitle: 'Models',
    modelsIntro: 'Enter your API keys to use models from the following providers.',
    modelsEdit: 'Edit',
    modelsAdd: 'Add provider',
    modelsProvider: 'Provider',
    modelsCancel: 'Cancel',
    modelsApply: 'Apply',
    modelsApplying: 'Applying…',
    modelsSavedProvider: 'Saved {provider}.',
    modelsCredentialConfigured: 'API key configured',
    modelsCredentialMissing: 'API key missing',
    modelsLoadFailed: 'Loading the provider directory failed',
    modelsReload: 'Try again',
    modelsKeyInput: 'API key',
    modelsKeyPlaceholder: 'Enter your API key',
    modelsKeyStored: 'Configured — enter a new value to replace',
    modelsCustomized: 'Customized settings',
    modelsBaseUrl: 'Base URL',
    modelsBaseUrlDefault: 'Provider default',
    modelsModels: 'Models',
    modelsSearchModels: 'Search models',
    modelsSearchModelsHint: 'Search by model name or ID',
    modelsNoMatchingModels: 'No matching models.',
    modelsAddModel: 'Add model',
    modelsRemoveModel: 'Delete model',
    modelsModelId: 'Model ID',
    modelsModelName: 'Display name',
    modelsModelNamePlaceholder: 'Uses the model ID when empty',
    modelsFetchModels: 'Fetch available models',
    modelsFetching: 'Asking the provider…',
    modelsFetchNeedsBaseUrl: 'Enter the base URL first, then fetch.',
    modelsFetchEmpty: 'The provider listed no models. Add them by hand.',
    modelsFetchTitle: 'Choose models to add',
    modelsFetchDescription: 'These are the models this provider has available. Choose the ones to add.',
    modelsFetchSelectAll: 'Select all',
    modelsFetchDeselectAll: 'Deselect all',
    modelsFetchAdopt: 'Add selected',
    modelsCustomAdd: 'Add a custom provider',
    modelsCustomTitle: 'Custom provider',
    modelsCustomTag: 'Custom',
    modelsCustomRoute: 'Provider ID',
    modelsCustomRouteHint: 'Lowercase identifier, starting with a letter, that uniquely names this provider in requests and as its credential name.',
    modelsCustomRouteInvalid: 'Start with a lowercase letter; then lowercase letters, digits, and dashes.',
    modelsCustomRouteTaken: 'A provider already uses this ID.',
    modelsCustomDisplayName: 'Display name',
    modelsCustomApi: 'API protocol',
    modelsCustomNeedsBaseUrl: 'A custom provider needs a base URL.',
    modelsCustomNeedsModels: 'A custom provider needs at least one model.',
    modelsCustomBaseUrlPlaceholder: 'https://gateway.example/v1',
    modelsCreate: 'Create provider',
    modelsCreating: 'Creating…',
    modelsOnboardingTitle: 'Add an API key to get started',
    modelsKeyRequired: 'Enter an API key to continue.',
    modelsSelectModel: 'Select model',
    modelsSelectVision: 'Select a vision model',
    modelsDefaults: 'Default models',
    modelsDeleteDescriptionWithCredential: 'Deleting {provider} removes its configuration and stored API key.',
    modelsAuthUnsupported: 'Native authentication is not configurable in this release',
    modelsGroupRecommended: 'Recommended',
    modelsGroupConfigured: 'Configured',
    modelsGroupCatalog: 'Catalog',
    modelsGroupCustom: 'Custom',
    modelsGroupUnsupported: 'Native authentication (not configurable)',
    modelsContextWindow: 'Context window',
    modelsMaxOutput: 'Max output',
    modelsInputText: 'Text',
    modelsInputImage: 'Image',
    modelsReasoning: 'Reasoning',
    modelsReasoningOff: 'Off',
    modelsDefaultEffort: 'Default effort',
    modelsRetry: 'Max retries',
    modelsSettingsConflict: 'Someone else changed these settings. Close and reopen to edit the current values.',
    modelsDiscovery401: 'Fetching available models returned 401.',
    modelsDiscoveryUnsupported: 'This protocol does not support model discovery.',
    modelsDiscoveryTooLarge: 'Provider response exceeded 4 MiB.',
    modelsDiscoveryMalformed: 'Provider returned malformed JSON.',
    modelsDiscoveryNetwork: 'Could not reach the provider. Check the URL and try again.',
    siliconOverrideModel: 'Vision model for this run',
    siliconEffort: 'Reasoning effort',
    siliconDisclaimer: 'Silicon samples are for pretest and hypothesis sketch. They never mix into human quota or default Results, and they do not prove that a design is scientifically valid.',
    siliconPersonas: 'Personas',
    siliconName: 'Name',
    siliconCity: 'City / country',
    siliconNotes: 'Scenario notes',
    siliconAddPersona: 'Add persona',
    siliconRemove: 'Remove',
    siliconRun: 'Run',
    siliconRepeats: 'Repeats',
    siliconBudget: 'Token budget',
    siliconDraftSnapshot: 'Each run snapshots the current saved draft and its project media.',
    siliconBudgetUsage: '{used} / {budget} tokens',
    siliconStart: 'Run silicon pretest',
    siliconNeedVlm: 'Uses your silicon VLM (must accept images). Set it under AI & Integrations.',
    siliconRuns: 'Runs',
    siliconCancel: 'Cancel',
    siliconCompare: 'Compare',
    siliconCount: 'Synthetic responses',
    siliconAnswers: 'answers',
    siliconPickPersona: 'Select at least one persona.',
    siliconResume: 'Continue unfinished units',
    siliconRetryFailed: 'Retry failed units only',
    siliconResumeHint: 'Keeps saved answers and only processes pending units.',
    siliconRetryHint: 'Resets failed or unknown units on this run. Does not start a new batch.',
    siliconReuseHint: 'Creates a new run with the same settings. Saved answers stay on the old run.',
    siliconUnverifiedQs: 'Listed but not preview-accepted',
    siliconErrorLease: 'Silicon background leases are not applied on this database.',
    draftSaveRpcMissing: 'Draft save is not ready on this database. Apply save_project_draft_revision_id.sql.',
    assistantRunSchemaMissing: 'Assistant run columns are not applied on this database.',
    siliconExport: 'Export JSON',
    siliconExportCsv: 'Export CSV',
    siliconDraftVersion: 'Draft version',
    siliconModelLabel: 'Model',
    siliconSelectedPersonas: 'Selected personas',
    siliconSupportedQs: 'Silicon can answer',
    siliconUnsupportedQs: 'Silicon will skip',
    siliconNoAnswerable: 'No answerable questions in the saved draft.',
    siliconSelectQuestions: 'Select at least one supported question before starting.',
    siliconSnapshotPages: 'snapshot pages',
    siliconSnapshotMedia: 'snapshot media',
    siliconCannotStart: 'No supported questions selected. Unsupported items are listed and will not be treated as a full-survey pass.',
    siliconPartialPretest: 'This is a partial pretest of supported questions only. Untested items are listed and a local pass is not a full-survey pass.',
    siliconOpenPersona: 'Open persona answers',
    siliconValidRate: 'Valid rate',
    siliconFailureReason: 'Failure',
    siliconShownMedia: 'Shown media',
    siliconStatusAllFailed: 'Finished — no valid answers',
    siliconStatusPartialValid: 'Finished — some answers invalid or skipped',
    siliconProgressCounts: 'Processed {done}/{total} trials · valid {answered} · skipped {skipped} · failed {errors} · remaining {remaining}',
    siliconStatusAllComplete: 'Finished with valid answers',
    aiChangeCardTitle: 'What changed',
    aiChangeVerified: 'Saved and verified',
    aiChangeSaved: 'Saved',
    aiChangeUnsaved: 'Not saved',
    aiChangeSaveFailed: 'Save failed',
    aiChangeVerifyIncomplete: 'Verification incomplete',
    aiChangeNoDiff: 'No field-level changes to show',
    aiWriteConflict: 'The editor has unsaved changes. Save before executing this request, or return to the editor.',
    aiSaveThenRun: 'Save then execute',
    aiReturnToEditor: 'Return to settings',
    aiQueueTitle: 'Queued tasks',
    aiQueueAfter: 'After this task',
    aiQueueNow: 'Steer current',
    aiQueueCancel: 'Cancel',
    siliconStatusPartial: 'Partial — budget stopped the run',
    siliconStatusBudgetFailed: 'Stopped — token budget exhausted',
    siliconStatusFailed: 'Failed',
    siliconStatusCancelled: 'Cancelled',
    siliconStatusRunning: 'Running',
    siliconStatusLoading: 'Loading counts…',
    siliconTasksTitle: 'Silicon tasks',
    siliconTasksTab: 'Tasks · {count}',
    siliconTasksBadge: 'Tasks · {count} running',
    siliconTasksActive: '{count} Silicon runs in progress',
    siliconTasksRecent: 'Finished',
    siliconTasksEmpty: 'No Silicon runs yet.',
    siliconTasksEmptyActive: 'No Silicon runs in progress.',
    siliconTasksEmptyEnded: 'No finished Silicon runs yet.',
    siliconTasksInProgress: 'In progress ({count})',
    siliconTasksEnded: 'Finished ({count})',
    siliconTasksBack: 'Back',
    siliconProcessTitle: 'Run process',
    siliconProcessSamples: 'Processed {done}/{total} trials',
    siliconProcessUnits: 'Processed {processed}/{total} trials: valid {valid}, skipped {skipped}, failed {failed}',
    siliconProcessCurrent: 'Current: {persona} · response {repeat} · {question} · trial {trial}/{trials}',
    siliconProcessWaiting: 'Waiting for the model · {seconds}s',
    siliconProcessTimeline: 'Technical details',
    siliconConfigSummary: '{personas} personas · {envelopes} simulated responses · {questions} questions, {trials} trials',
    siliconProcessedTrials: 'Processed {done}/{total} trials',
    siliconEndedNoAnswers: 'Finished, no valid answers',
    siliconTrialBreakdown: 'Processed {done}/{total} trials: valid {valid}, skipped {skipped}',
    siliconNoImagesReason: 'Reason: no usable images were resolved',
    siliconReuseSettings: 'Reuse settings and run again',
    siliconMediaPreviewLibrary: 'This run uses the platform preview library · {count} images available',
    siliconMediaProject: 'This run uses project media · {count} images available',
    siliconMediaNone: 'No usable media source for this run',
    siliconEnvelopeLabel: '{persona} · response {repeat}',
    siliconQuestionCard: 'Question {n} · {title}',
    siliconTrialProgress: 'Processed {done}/{total} trials · now on trial {current}',
    siliconWaitingStart: 'Waiting to start',
    siliconTechDetails: 'Technical details',
    siliconEndedAt: 'Ended {time}',
    siliconDuration: 'Ran {duration}',
    siliconDurationSeconds: '{seconds}s',
    siliconDurationMinutes: '{minutes}m {seconds}s',
    siliconStatusQueued: 'Queued',
    siliconStatusThrottled: 'Waiting for rate limit',
    siliconStatusStopping: 'Stopping',
    siliconErrorNoMedia: 'No usable media source. The draft has no project media and the preview library is empty.',
    siliconErrorFolderEmpty: 'The selected folders have no matching images.',
    siliconErrorImageUnreadable: 'An image URL could not be read.',
    siliconErrorModel: 'The model request failed.',
    siliconViewProcess: 'View process',
    siliconViewAnswers: 'View saved answers',
    siliconOpenProject: 'Open this project',
    siliconStopping: 'Stopping…',
    siliconConnectionLost: 'Connection interrupted. The task is still on the server — this is not a task failure.',
    siliconTaskFinished: 'Silicon run for {project} is {status}.',
    siliconTaskCounts: '{processed}/{total} trials · {valid} valid · {failed} failed',
    siliconRationale: 'Answer basis',
    siliconEventStart: 'Started this question',
    siliconEventMedia: 'Prepared images for this trial',
    siliconEventRequest: 'Requesting the model',
    siliconEventResponse: 'Received an answer',
    siliconEventValid: 'Answer passed validation',
    siliconEventInvalid: 'Answer failed validation',
    siliconEventSaved: 'Answer saved',
    siliconEventSkip: 'Skipped',
    siliconEventRetry: 'Retrying',
    siliconEventThrottle: 'Queued / rate limited',
    siliconEventStopping: 'Stopping',
    siliconEventComplete: 'Run finished',
    siliconEventFailed: 'Run failed',
    siliconStageQueued: 'Queued',
    siliconScienceNote: 'These counts are a synthetic pretest, not evidence that the instrument is scientifically valid.',
    featureAssistant: 'In-browser Assistant',
    featureAssistantHint: 'Independent of Silicon. Turn off to hide the Assistant sidebar.',
    featureSilicon: 'Silicon pretest (experimental)',
    featureSiliconHint: 'Independent of the Assistant. Keep off until the pretest contract is ready for a study.',
    qualityChecks: 'Run design checks',
    qualityChecksEmpty: 'No on-demand design issues found.',
    qualityCheckResearch: 'Research design',
    qualityCheckFlow: 'Participant flow',
    qualityCheckExport: 'Analysis / export',
    resultsSiliconHint: 'Human results only by default. Open the Silicon Samples tab to compare synthetic VLM answers.',

    // Public header / footer
    navPaperLibrary: 'Paper library',
    navNews: 'News',
    navRequestTemplate: 'Request template',
    navRequestDesign: 'Request design',
    navTeam: 'Team',
    navLiveSurveys: 'Live surveys',
    navSpBench: 'SP-Bench',
    navResearcherLogin: 'Researcher login',
    footerDevelopedBy: 'Developed by',

    newsTitle: 'News',
    newsSubtitle: 'Product updates, releases, and research notes from SP-Survey.',
    newsEmpty: 'No posts yet. Check back soon.',
    newsBackToList: 'All news',
    newsNotFound: 'This post was not found or is no longer published.',
    newsNoBody: 'No content.',
    newsUnavailable: 'News is not available yet.',

    benchNotOpenTitle: 'SP-Bench',
    benchNotOpenBody: 'This benchmark is not open to the public yet.',
    benchMethod: 'Method',
    benchDataset: 'Dataset',
    benchSamples: 'samples',
    benchModels: 'models',
    benchOverall: 'Overall',
    benchObjective: 'Objective',
    benchSubjective: 'Subjective',
    benchCognition: 'Cognition',
    benchMethodTab: 'Methodology',
    benchLandingFallback: 'Benchmarking subjective–objective spatial perception and cognition in urban environments.',
    benchNoResultsYet: 'Leaderboard coming soon',
    benchViewLeaderboard: 'View leaderboard',

    // Landing page
    landLiveBannerOne: '{n} live survey is online now — take it as a participant.',
    landLiveBannerMany: '{n} live surveys are online now — take them as a participant.',
    landBrowseLive: 'Browse Live Surveys',
    landHeroTitle: 'Streetscape Perception Survey',
    landHeroLine1: 'A research-grade platform for conducting visual perception surveys on urban streetscapes.',
    landHeroLine2: 'Build, share, and analyze streetscape perception studies — no coding required.',
    landStartFree: 'Start for Free →',
    landReadPaper: 'Read Paper',
    landFeaturesTitle: 'Everything you need for perception research',
    landFeatUploadTitle: 'Upload Images',
    landFeatUploadDesc: 'Upload street-view images directly to cloud storage. Auto-compressed to keep surveys fast.',
    landFeatAiTitle: 'AI Survey Builder',
    landFeatAiDesc: 'Describe your research goals and let ChatGPT generate a complete survey with validated question types.',
    landFeatShareTitle: 'Instant Sharing',
    landFeatShareDesc: 'Get a shareable link immediately — no server setup, no deployment, no configuration.',
    landFeatResultsTitle: 'Results Analysis',
    landFeatResultsDesc: 'View responses per question with image–response pairing and export to CSV.',
    landTemplatesTitle: 'Template Library',
    landTemplatesSubtitle: 'Start from peer-reviewed survey designs used in published research',
    landRequestTemplate: 'Request a Template for Your Paper',
    landRequestDesign: 'Request Survey Design Help',
    landSearchTemplates: 'Search templates by name, author, or topic...',
    landCatAcademic: 'Academic Research',
    landCatUrban: 'Urban Theory',
    landCatAi: 'AI Generated',
    landSearchCategoryHint: 'Search to explore all {n} templates in this category.',
    landNoTemplates: 'No templates found for "{q}"',
    landCtaTitle: 'Ready to start your survey?',
    landCtaBody: 'Create an account and launch your first survey in under 10 minutes.',
    landGetStarted: 'Get Started Free',
    landPreview: 'Preview',
    landUse: 'Use',
    landPaper: 'Paper',
    landDataset: 'Dataset',
    landChipAi: 'AI',
    landChipUrban: 'Urban',
    landChipAcademic: 'Academic',
    landPreviewTitle: 'Preview — {name}',
    landReadOnly: '(read-only)',
    landClose: 'Close',

    trialDoNTimes: 'You will do this {n} times. This is {current} of {n}.',
    trialProgressCount: 'Completed {done} / {n}. {left} remaining.',
    trialAutoAdvance: 'After you choose, the next round starts automatically.',
    trialUseNext: 'Use Next round after you answer.',
    trialLastRound: 'Last round — then continue the survey.',
    trialAllDone: 'All {n} rounds answered.',
    trialContinue: 'Continue to next question',
    trialFinishSurvey: 'Finish survey',
    trialBack: 'Back',
    trialNextRound: 'Next round',
    trialPageNextBlocked: 'Please complete all rounds before continuing.',
    progressRound: 'Round {current} / {n}',
    trialLabel: 'Round {n}',
    trialReviewAnswers: 'Review answers',
    progressPage: 'Page {current} / {n}',
    progressQuestion: 'Question {current} / {n}',
    progressQuestionLabel: 'Question {n}',
    progressTrialCount: '{done}/{n} rounds',
    progressNotReached: 'Not reached yet',
    progressJump: '{label} — click to jump',
    progressPreviousGroup: 'Previous question group',
    progressNextGroup: 'Next question group',
    participantSaving: 'Saving your responses…',
    participantThanks: 'Thank you!',
    participantCompleted: 'Thank you for completing the survey!',
    participantRoundsCompleted: 'All {n} annotation rounds completed!',
    participantCompletionCode: 'Completion code:',
    participantIdLabel: 'Your participant ID:',
    participantSavedLocally: 'Responses saved locally.',
    participantSaved: 'Responses saved successfully.',
    participantSubmitted: 'Your answers were submitted.',
    participantSaveError: 'We could not save your responses due to a network or server issue. Your answers are preserved — please try again.',
    participantRetrySubmission: 'Retry submission',
    participantResumeTitle: 'Resume previous session?',
    participantResumeBody: 'We found an unfinished survey from {date}. Would you like to continue where you left off?',
    participantPreviousVisit: 'a previous visit',
    participantStartFresh: 'Start fresh',
    participantResume: 'Resume',

    // Login
    loginBrand: 'Streetscape Perception Survey',
    loginSubtitle: 'Researcher login',
    loginSignIn: 'Sign In',
    loginCreateAccount: 'Create Account',
    loginEmail: 'Email',
    loginPassword: 'Password',
    loginPasswordHelp: 'Minimum 6 characters',
    loginSigningIn: 'Signing in...',
    loginCreating: 'Creating account...',
    loginRegisterSuccess: 'Registration successful! Please check your email to confirm your account, then log in.',
    loginErrorGeneric: 'An error occurred',
    loginBackHome: '← Back to home',

    // Live surveys
    liveTitle: 'Live Surveys',
    liveBody: 'Community studies currently featured by the platform. Participant links follow the researcher’s release settings. Grey cards are outside their approved time window.',
    liveEmpty: 'No live surveys are featured yet. Researchers can apply from their project menu (Publish to Main Page); admins approve listings on the admin dashboard.',
    liveNowOnline: 'Now online ({n})',
    liveUpcomingClosed: 'Upcoming & closed',
    liveOnline: 'Online',
    liveUpcoming: 'Upcoming',
    liveClosed: 'Closed',
    liveNoDescription: 'No description provided.',
    liveTakeSurvey: 'Take survey',
    liveNotOpenYet: 'Not open yet',

    // Team
    teamTitle: 'Team',
    teamIntroBefore: 'SP-Survey is led by',
    teamIntroMid: 'at the',
    teamIntroAfter: ', National University of Singapore, with collaborators across partner institutions and supervised by Filip Biljecki.',
    teamCollaborators: 'Collaborators',
    teamAffiliations: 'Affiliations',
    teamProfile: 'Profile',
    teamProjectLead: 'Project Lead',
    teamPI: 'Principal Investigator',
    teamLeadBlurb: 'Initiated and leads the design, development, and open release of SP-Survey — from platform architecture and survey tooling to the hosted research workflow.',
    teamPiBlurb: 'Founder of the Urban Analytics Lab; provides academic supervision and research direction for SP-Survey.',
    teamRolePhd: 'PhD Researcher',
    teamRoleEngineer: 'Research Engineer',
    teamRoleFellow: 'Research Fellow',
    teamRoleVisiting: 'Visiting Scholar',
    teamRoleAssocProf: 'Associate Professor',

    // Papers library (shell)
    papersTitle: 'Paper library',
    papersCount: '{n} papers',
    papersWithTemplate: '{n} with template',
    papersWithTemplateTitle: 'Show only papers linked to a survey template',
    papersBody: 'Curated urban imagery × human-survey literature used to ground survey templates on this platform. Linked template IDs appear when admins match a paper to an existing survey template.',
    papersAll: 'All papers',
    papersHasTemplate: 'Has template ({n})',
    papersNoTemplate: 'No template ({n})',
    papersSearchPlaceholder: 'Title / DOI / venue / author / template id',
    papersSearch: 'Search',
    papersClearSearch: 'Clear search',
    papersShowing: 'Showing {shown} / {total} papers',
    papersHasTemplateSuffix: ' · has template',
    papersNoTemplateSuffix: ' · no template',
    papersNoMatch: 'No matching papers.',
    papersOpen: 'Open paper',
    papersColTitle: 'Title',
    papersColYear: 'Year',
    papersColTemplate: 'Template',
    papersColLink: 'Link',

    // Shared request form chrome
    reqCopied: 'Copied',
    reqCopy: 'Copy',
    reqSubmitAnother: 'Submit another',
    reqBackHome: 'Back to home',
    reqStarting: 'Starting…',
    reqUploading: 'Uploading ({current}/{total})…',
    reqSubmitting: 'Submitting…',
    reqSubmit: 'Submit request',
    reqChooseFiles: 'Choose files',
    reqClear: 'Clear',
    reqFilesSelected: '{n} file(s) selected',
    reqMore: '+{n} more',
    reqMax: 'max {n}',
    reqUploaded: ' uploaded.',
    reqFileTooLarge: '"{name}" is too large (max {mb} MB).',
    reqUploadUnavailable: 'File upload is not available right now. Submit without files, or try again later.',

    // Request template
    reqTplTitle: 'Request a Template for Your Paper',
    reqTplIntro: 'No account needed. Share your paper details, optional survey dataset images, and supplementary files (PDF, etc.). We create a pending template ID you can use after review. Need help designing a custom study instead?',
    reqTplLinkDesign: 'Request survey design',
    reqTplSuccess: 'Request submitted. Your pending template ID is ready — save it.',
    reqTplId: 'Template ID',
    reqTplImagesCount: '{n} survey dataset image(s)',
    reqTplSuppCount: '{n} supplementary file(s)',
    reqTplStatus: 'Status: pending review (not on the public library yet). After approval, open the researcher panel and use this template ID.',
    reqTplNeedFields: 'Please fill in paper title, authors, and paper link.',
    reqTplBadYear: 'Year must be a 4-digit year (e.g. 2025).',
    reqTplBadEmail: 'Please enter a valid email, or leave it blank.',
    reqTplPaperTitle: 'Paper title',
    reqTplAuthors: 'Author name(s)',
    reqTplAuthorsHelp: 'e.g. Yang, Sijie and Chong, Adrian and Liu, Pengyuan and Biljecki, Filip',
    reqTplYear: 'Year',
    reqTplYearPh: 'e.g. 2025',
    reqTplLink: 'Paper link',
    reqTplLinkPh: 'https://doi.org/… or publisher URL',
    reqTplEmail: 'Email (optional)',
    reqTplEmailHelp: 'Optional — so we can reach you about this request. No account required.',
    reqTplNotes: 'Notes (optional)',
    reqTplNotesHelp: 'Method details, survey design hints, Hugging Face dataset, etc.',
    reqTplImages: 'Survey dataset images (optional)',
    reqTplImagesHelp: 'Images are auto-compressed before upload.',
    reqTplChooseImages: 'Choose images',
    reqTplSupp: 'Supplementary files (optional)',
    reqTplSuppHelp: 'PDF, Word, slides, spreadsheets, ZIP, etc. — up to {mb} MB each.',

    // Request survey design
    reqDesTitle: 'Request Survey Design',
    reqDesIntro: 'Planning a perception study and want help turning it into an SP-Survey? Share a short research brief — this is best-effort research collaboration, not a paid service. Prefer to convert a published paper into a reusable template?',
    reqDesLinkTpl: 'Request a template instead',
    reqDesSuccess: 'Request submitted. Save your request ID — we will follow up by email when we can help.',
    reqDesId: 'Request ID',
    reqDesMediaCount: '{n} media file(s)',
    reqDesSuppCount: '{n} supplementary file(s)',
    reqDesStatus: 'Status: pending. We review requests as capacity allows and will email you if we can help design or scaffold your survey.',
    reqDesStartSelf: 'Or start building yourself',
    reqDesNeedFields: 'Please fill in name, email, study title, and research brief.',
    reqDesBadEmail: 'Please enter a valid email so we can follow up.',
    reqDesBriefShort: 'Please describe your study in a bit more detail (at least a few sentences).',
    reqDesName: 'Your name',
    reqDesEmail: 'Email',
    reqDesEmailHelp: 'Required so we can follow up.',
    reqDesAffiliation: 'Affiliation (optional)',
    reqDesAffiliationPh: 'e.g. Urban Analytics Lab, NUS',
    reqDesStudyTitle: 'Study title',
    reqDesBrief: 'Research brief',
    reqDesBriefHelp: 'What do you want to measure, with whom, and roughly how? A few sentences is enough.',
    reqDesStimulus: 'Stimulus media (optional)',
    reqDesStimulusHelp: 'What participants will see or hear.',
    reqDesStimImage: 'Images',
    reqDesStimVideo: 'Video',
    reqDesStimAudio: 'Audio',
    reqDesStimMixed: 'Mixed media',
    reqDesStimOther: 'Other / not sure yet',
    reqDesTimeline: 'Timeline (optional)',
    reqDesTimelinePh: 'e.g. pilot in August, full run in October',
    reqDesRelated: 'Related link (optional)',
    reqDesRelatedPh: 'Paper, project page, or dataset URL',
    reqDesNotes: 'Additional notes (optional)',
    reqDesNotesHelp: 'Question types you have in mind, sample size targets, constraints, etc.',
    reqDesSampleMedia: 'Sample media (optional)',
    reqDesSampleHelp: 'Images, video, or audio examples. Images are auto-compressed before upload.',
    reqDesChooseMedia: 'Choose media',
    reqDesSupp: 'Supplementary files (optional)',
    reqDesSuppHelp: 'Protocol notes, PDF, slides, spreadsheets, ZIP, etc. — up to {mb} MB each.',
    reqDesInfo: 'Best-effort support from the SP-Survey team as capacity allows. You can also create an account and build the survey yourself anytime.',

    // Legacy / China
    regionLabel: 'Region',
    globalMode: 'Global',
    chinaMode: 'China',
    languageToggle: '中文',
    imageDataset: 'Step 1 - Media Dataset',
    datasetProvider: 'Dataset Provider',
    huggingface: 'Hugging Face',
    modelscope: 'ModelScope (魔搭)',
    msToken: 'ModelScope Access Token (Optional)',
    hfDatasetName: 'HuggingFace Dataset Name',
    msDatasetName: 'ModelScope Dataset Name',
    msPlaceholder: 'e.g. sijiey/Thermal-Affordance-Dataset',
    hfTokenHelp: 'Optional: only for private datasets. Get from huggingface.co/settings/tokens',
    msTokenHelp: 'Optional: only for private datasets. Get from modelscope.cn/my/myaccesstoken',
    hfBrowse: 'Browse HuggingFace Datasets',
    msBrowse: 'Browse ModelScope Datasets',
    deploymentGuide: 'Deployment Guide',
    globalDeployment: 'Vercel Deployment',
    chinaDeployment: 'Zeabur Deployment (China)',
    storageProvider: 'Image Storage Provider',
    supabase: 'Supabase Storage',
    aliyunOss: 'Alibaba Cloud OSS',
    chinaModeBanner: 'China Mode enabled — Using ModelScope, Alibaba Cloud OSS, and Zeabur',
  },

  zh: {
    tabIntro: '简介',
    tabMedia: '1. 媒体',
    tabBuilder: '2. 设计',
    tabHost: '3. 部署',
    tabShare: '4. 分享',
    tabResults: '5. 结果',
    tabPractice: '试作',
    tabSilicon: '硅基',
    siliconPageTitle: '硅基样本预试',

    toggleSidebar: '打开/关闭项目侧栏',
    saveTooltipDirty: '保存草稿修改',
    saveTooltip: '保存',
    aiTooltip: '打开 AI 助手侧栏 — 模型与 MCP 仍在设置中管理',
    aiLabel: 'AI',
    toggleAiSidebarOpen: '关闭 AI 助手',
    toggleAiSidebarClosed: '打开 AI 助手',
    aiSidebarTitle: 'AI 助手',
    aiSidebarSubtitle: '通过对话设计当前项目',
    aiSidebarSelectProject: '请先选择一个项目再发送消息。',
    aiSidebarNoProject: '请先选择项目再开始对话。',
    aiSidebarNotConnected: '未连接 — 打开设置添加 API 密钥。',
    aiSidebarModelUnavailable: '发送前请选择已配置的模型。之前的提供商可能已被移除。',
    aiSidebarSettings: '助手设置',
    aiSidebarSilicon: '硅基预测试',
    aiSidebarClear: '清空历史',
    aiSidebarDownload: '下载对话',
    aiSidebarClose: '关闭 AI 侧栏',
    aiSidebarMore: '更多操作',
    aiSidebarEmptyTitle: '让助手帮你设计这份问卷',
    aiSidebarEmptyReady: 'AI 助手已就绪。描述你想要的问卷或修改即可。',
    aiSidebarEmptyConnect: '打开设置连接 API 密钥，或通过集成使用 Codex / MCP。',
    aiSidebarComposerPlaceholder: '输入消息…（例如「创建一份热舒适问卷」）',
    aiSidebarComposerDisabled: '请先选择项目并配置模型。',
    aiSidebarUndo: '撤销上一次 AI 修改',
    aiSidebarConnected: '已连接',
    aiSidebarDisconnected: '未连接',
    aiSidebarOpenIntegrations: '管理模型与 MCP',
    aiSidebarModel: '模型',
    aiSidebarReasoning: '推理',
    aiSidebarMode: '助手模式',
    aiSidebarModeAgent: '智能体',
    aiSidebarModeGenerate: '生成',
    aiSidebarModeAdjust: '调整',
    aiSidebarModeQuestion: '问答',
    aiSidebarSelectModel: '选择模型',
    aiSidebarConfigureModel: '请先配置模型',
    aiSidebarAssistantName: '助手',
    aiSidebarSend: '发送消息',
    aiSidebarStop: '停止当前任务',
    aiSidebarApprovalPrompt: '此操作需要你的批准',
    aiSidebarApprovalRisk: '风险：{risk}',
    aiSidebarApprovalProject: '项目：{project}',
    aiSidebarApprovalApprove: '批准',
    aiSidebarApprovalDeny: '拒绝',
    aiSidebarSteer: '调整方向',
    aiSidebarSteerPlaceholder: '给正在运行的智能体补充指令…',
    aiSidebarStatusThinking: '正在思考…',
    aiSidebarStatusQueued: '已排队，等待开始…',
    aiSidebarStatusRunning: '正在继续生成问卷…',
    aiSidebarStatusApproval: '等待你的批准…',
    aiSidebarStatusRetrying: '正在重试模型请求…',
    aiSidebarStatusCompacting: '正在压缩上下文并继续…',
    aiSidebarStatusVerifying: '正在核对刚保存的草稿…',
    aiSidebarStatusUsingTool: '正在调用 {tool}…',
    aiSidebarStatusStep: '正在执行第 {step} 步…',
    aiSidebarElapsed: '已用 {seconds} 秒',
    aiSidebarToolRunning: '进行中',
    aiSidebarToolDone: '完成',
    aiSidebarToolError: '失败',
    aiSidebarToolUnknown: '未知',
    aiSidebarMemoryHint: '记忆按项目隔离 •',
    aiSidebarUnnamedProject: '未命名项目',
    aiSidebarCodexTitle: 'Codex / MCP',
    aiSidebarCodexBody: '面向 Cursor、Claude 和 ChatGPT 的高级 MCP 路径。',
    aiSettingsTitle: '助手设置',
    aiSettingsModels: '模型',
    aiSettingsContext: '研究背景',
    aiSettingsAdvanced: '高级',
    aiSettingsLocalKeyIntro: '为当前浏览器连接 OpenAI 或 OpenRouter API 密钥。',
    aiSettingsValidate: '验证',
    aiSettingsContextTitle: '研究背景',
    aiSettingsContextIntro: '这些信息是可选的，用于让助手在当前项目中保持方向一致。',
    aiSettingsMemory: '项目记忆',
    aiSettingsMemoryHint: '仅在当前项目内保留对话背景和偏好。',
    aiSettingsMemoryAutomatic: '当前项目的对话连续性由托管 Agent 会话自动处理。',
    aiSettingsResearchTopic: '研究主题',
    aiSettingsResearchRequirements: '研究要求',
    aiSettingsScenario: '调查场景',
    aiSettingsCustomScenario: '添加自定义场景',
    aiSettingsAdd: '添加',
    aiSettingsAdvancedTitle: '高级',
    aiSettingsAdvancedIntro: '实验性与维护功能。大多数研究无需调整。',
    aiSettingsMultiAgent: '多智能体审查',
    aiSettingsExperimental: '实验性',
    aiSettingsMultiAgentUnavailable: '当前托管 Agent Runtime 尚不支持旧版多智能体流式审查。该功能仍保留给自托管开发环境，但不再出现在日常工作流中。',
    aiSettingsMultiAgentToggle: '使用多个专家提示审查生成的问卷',
    aiSettingsIndependentReview: '独立审查',
    aiSettingsGroupReview: '小组讨论',
    aiSettingsReviewRounds: '最多审查轮数',
    aiSettingsEditAgents: '编辑专家提示',
    aiSettingsPrompts: '系统提示词',
    aiSettingsPromptsHint: '仅用于旧版本地助手',
    aiSettingsPromptGenerate: '生成问卷提示词',
    aiSettingsPromptAdjust: '调整问卷提示词',
    aiSettingsPromptIntent: '意图识别提示词',
    aiSettingsPromptQuestion: '问答提示词',
    aiSettingsReset: '重置',
    aiSettingsSave: '保存',
    aiSettingsConversationData: '对话数据',
    aiSettingsConversationDataHint: '下载当前对话，或从当前项目中清空它。',
    aiClearHistoryTitle: '清空对话',
    aiClearHistoryMessage: '清空此项目的对话历史？',
    aiClearHistoryConfirm: '清空',
    previewSurvey: '预览问卷',
    viewLive: '问卷链接',
    moreTools: '更多工作区工具',
    signedInAs: '当前登录',
    changeTheme: '切换主题',
    skillsLibrary: '自定义交互',
    adminDashboard: '管理后台',
    templatesAndProjects: '模板与项目',
    clearEditingState: '清除编辑状态',
    signOut: '退出登录',
    chooseTheme: '选择主题',
    loadingProjectSystem: '正在加载项目系统…',
    loadingWorkspace: '正在加载工作区…',
    noProjectTitle: '未选择项目',
    noProjectBody: '请在侧栏新建项目，或选择已有项目开始。',
    openProjectSidebar: '打开项目侧栏',
    saveStatusSaving: '保存中…',
    saveStatusError: '保存失败 — 点击保存重试',
    saveStatusUnsaved: '有未保存更改',
    saveStatusJustNow: '刚刚自动保存',
    saveStatusSecsAgo: '{n} 秒前自动保存',
    saveStatusMinsAgo: '{n} 分钟前自动保存',
    saveStatusAllSaved: '全部已保存',

    introTitle: 'Admin 工作区怎么用',
    introBody:
      '优先使用右侧 AI 助手侧栏设计问卷（你自己的 API 密钥）。Codex / Cursor 的 MCP 是可选的高级入口。',
    introStep1Title: '步骤 1 — 媒体数据集',
    introStep1Blurb: '上传并整理图片、视频、音频；为 set/category 模式给文件夹打标签。',
    introStep2Title: '步骤 2 — 问卷构建',
    introStep2Blurb: '设计页面与题目；保存只更新草稿。已启用版本管理的项目需再次发布才会影响线上问卷。',
    introStep3Title: '步骤 3 — 部署',
    introStep3Blurb: '配置 Supabase，发布参与者快照，再部署参与者站点并保存其地址。',
    introStep4Title: '步骤 4 — 分享问卷',
    introStep4Blurb: '复制问卷链接、下载二维码，并发出问卷前先试跑一次。',
    introStep5Title: '步骤 5 — 结果分析',
    introStep5Blurb: '浏览答卷、导出数据，并对本项目进行分析。',
    introAddonTitle: '附加 — 研究者练习',
    introAddonBlurb: '先自己答一遍问卷，检查流程与媒体，再邀请参与者。',
    introFlowTitle: '推荐：站内 AI 助手',
    introFlowLead:
      '先保存 DeepSeek / OpenRouter / OpenAI 密钥，再从任意 Admin 标签的右侧边栏和助手对话。MCP（Codex、Cursor、Claude）给已经习惯那些工具的人用。',
    introFlow1Title: '保存 API 密钥并选择模型',
    introFlow1Body:
      '打开「AI 与集成」，添加支持的 Provider 密钥，再为 AI 助手和硅基预试选择默认模型。',
    introFlow2Title: '让 AI 助手构建问卷',
    introFlow2Body:
      '打开右侧 AI 助手，描述研究主题与设计。助手会用带冲突保护的增量操作修改当前项目草稿。',
    introFlow3Title: '若未添加媒体则补上',
    introFlow3Body:
      '需要图片/视频/音频时，请在「媒体」页签上传文件或从 Hugging Face 导入；不要生成合成媒体再上传。set/category 模式还需给文件夹打标签。',
    introFlow4Title: '先部署，再分享',
    introFlow4Body:
      '在「部署」页签配置 Supabase、发布参与者快照并部署参与者站点。然后到「分享」复制链接。旧项目仅在第一次发布前保持保存即上线的兼容行为。',
    introFlow5Title: '分析结果',
    introFlow5Body:
      '有数据后，让 Codex 分析指定项目。它可汇总、导出 JSON/CSV 并协助解读——也可打开 Admin 步骤 5 结果分析。',
    connectCodex: '设置 AI 助手',
    openSurveyBuilder: '打开问卷构建',
    openMediaDataset: '打开媒体数据集',
    openHost: '打开部署',
    openShareSurvey: '打开分享问卷',
    openResultsAnalysis: '打开结果分析',
    nextHost: '下一步：部署 →',

    mediaTitle: '媒体数据集',
    mediaDescription:
      '添加媒体、按文件夹整理，再标记为分组或分类，供问卷分配使用。文件存储在你的 Supabase Storage 存储桶中。',
    mediaAddSection: '1 · 添加媒体',
    mediaOrganizeSection: '2 · 在文件夹中整理',
    mediaTaggedSection: '3 · 已标记供分配',
    mediaUploadTitle: '上传媒体',
    mediaUploadHelpPrefix: '上传到媒体库中的当前文件夹',
    mediaUploadLimitsHelp:
      '单文件上限 {mb} MB。图片可选择压缩；视频、音频和动态 GIF 保留原文件。',
    mediaAvTooLarge: '「{name}」过大（{size} MB）。视频/音频上限 {mb} MB。',
    mediaChooseFiles: '选择文件',
    mediaSelected: '已选',
    mediaUploadBtn: '上传',
    mediaUploadRoot: '→ 根目录',
    mediaNoFoldersTagged:
      '尚未标记文件夹。在媒体库左侧勾选文件夹，再点击“分组”或“分类”。标记后的文件夹会显示在这里，供问卷分配使用。',
    mediaTaggedSets: '已标记分组（{n}）',
    mediaTaggedSetsHelp:
      '标记为分组的文件夹。题目使用“随机固定分组”且媒体数量匹配时，文件夹内直属文件会一起展示。',
    mediaTaggedCategories: '已标记分类（{n}）',
    mediaTaggedCategoriesHelp: '标记为分类的文件夹。题目可从每个分类中随机抽取文件。',
    mediaCheckingR2: '正在检查存储中的现有图片…',
    mediaLastUpload: '最近上传：',
    hfImportTitle: 'HF 数据集导入',
    hfImportHelp:
      '从 HuggingFace 批量导入。使用 owner/dataset（按行）或 owner/dataset/folder（递归；保留嵌套文件夹）。继续导入会复用首次目标路径，避免重复嵌套。',
    hfEnable: '启用 HF 导入',
    hfToken: '令牌（可选）',
    hfDataset: '数据集',
    hfPlaceholder: 'owner/dataset',
    saveConfig: '保存',
    testConnection: '测试',
    hfPreload: '预加载到存储',
    hfRePreload: '重新预加载到存储',
    noMediaYet: '尚未上传媒体',
    mediaInR2: '个媒体文件在存储中',

    guideFixedSets: '固定分组',
    guideFixedSetsBody:
      '将文件夹标记为分组（Set）。每个分组文件夹的直属文件会一起展示，每组文件数须与题目的媒体数量一致。',
    guideExample: '示例',
    guideFixedSetsExample:
      '1. 创建文件夹 block01、block02、block03\n2. 每个文件夹放 2 张图（数量相同）\n3. 勾选这些文件夹 → 点击“分组”\n4. 在问卷构建中选择媒体分配方式「随机抽取完整分组」，数量 = 2\n→ 每位受访者看到一整文件夹（两张图一起）',
    guideTaggedSets: '当前已标记分组：{n}',
    guideCategories: '分类',
    guideCategoriesBody:
      '将文件夹标记为分类（Category）。在题目中选择「按分类抽取」，并设置每个分类抽取的文件数。每个分类会从该文件夹及其子文件夹中随机抽取。',
    guideCategoriesExample:
      '1. 创建文件夹 street 与 park\n2. 每个放若干图片（可用子文件夹）\n3. 勾选这些文件夹 → 点击“分类”\n4. 在题目中：媒体分配 → 按分类抽取，每类文件数 = 2\n→ 每位受访者看到 2 张 street + 2 张 park（共 4 张）',
    guideTaggedCategories: '当前已标记分类：{n}',
    guideProjectFiles: ' · 项目文件：{n}',

    builderTitle: '问卷构建',
    builderDescription:
      '用页面、题目与主题构建问卷。可用下方 AI 助手起草与修改题目，分享前请先预览。',
    builderSurveySettings: '问卷设置',
    builderBasicInfo: '📝 基本信息',
    builderSurveyTitle: '问卷标题',
    builderSurveyTitleHelp: '显示在问卷顶部的主标题',
    builderSurveyDescription: '问卷说明',
    builderSurveyDescriptionHelp: '简要说明问卷目的',
    builderCompletionMessage: '完成提示（可选）',
    builderCompletionMessageHelp: '参与者提交后显示的内容',
    builderResponseQuota: '答卷配额（可选）',
    builderResponseQuotaHelp: '达到该答卷数后自动关闭问卷（留空表示不限）',
    builderLogoSettings: '🖼️ Logo 设置',
    builderDisplaySettings: '⚙️ 显示设置',
    builderSurveyLanguage: '问卷语言',
    builderSurveyLanguageHelp: '参与者看到的进度、轮次提示、下一页/提交按钮。默认英文。',
    builderSurveyLanguageEn: 'English',
    builderSurveyLanguageZh: '中文',
    builderThemeCustomization: '🎨 主题自定义',
    builderPagesQuestions: '页面与题目',
    builderPagesHelp: '将问卷组织为多个页面。可拖拽页面调整顺序。',
    builderAddPage: '添加新页面',

    hostTitle: '部署',
    hostDescription: '配置 Supabase，发布参与者快照，再部署参与者站点并保存其地址。',
    hostBackendTitle: '后端 / Supabase',
    hostStepperTitle: '部署参与者站点',
    hostStepperHelp: '准备部署文件夹、测试构建、推送到 GitHub、填写 Vercel 环境变量，再完成上线前检查。',
    hostDeployedUrlLabel: '已部署的参与者地址',
    hostDeployedUrlHelp: '部署完成后粘贴公开的 Vercel（或其他）地址。「分享」页签会用它生成链接和二维码。',
    hostDeployedUrlPlaceholder: 'https://your-project.vercel.app',
    hostDeployedUrlSave: '保存地址',
    hostDeployedUrlSaved: '已保存',
    serverTitle: '后端 / Supabase',
    serverDescription: '配置用于存储问卷回答的 Supabase 数据表。',

    shareTitle: '分享问卷',
    shareDescription: '复制问卷链接、下载二维码，并发出问卷前先试跑一次。',
    shareGoToHost: '打开部署',
    shareDeployedHint: '这是「部署」页签中保存的参与者站点地址。',
    shareYourLink: '你的问卷链接',
    shareCopied: '已复制！',
    shareCopyLink: '复制链接',
    shareOpenSurvey: '打开问卷',
    shareQrAlt: '问卷二维码',
    shareQrDownload: '下载二维码（PNG）',
    shareQrHint: '扫码打开此问卷。可下载 1024 × 1024 PNG，用于分享或打印。',
    shareQrLoading: '正在生成二维码',
    shareQrError: '二维码生成失败。',
    shareQrRetry: '重新生成',
    shareQrLocalHint: '当前链接使用 localhost，其他手机无法访问。请先到「部署」页签发布参与者站点并保存地址，再回到这里复制可分享链接和二维码。',
    shareNoProject: '未选择项目。请先选择或创建项目。',
    shareTips: '💡 分享提示',
    shareTip1Primary: '直接分享链接',
    shareTip1Secondary: '可通过邮件、微信、Slack 等渠道发送',
    shareTip2Primary: '做成二维码',
    shareTip2Secondary: '在上方“你的问卷链接”中下载二维码，可用于邀请、幻灯片或印刷材料',
    shareTip3Primary: '同一个研究使用同一个链接',
    shareTip3Secondary: '启用版本管理后，发布正式版本才会更新链接；保存只更新草稿。',
    shareTip4Primary: '答卷自动保存',
    shareTip4Secondary: '结果实时写入 Supabase，可在结果分析标签页查看',
    shareAdminLink: '🔒 管理面板链接',
    shareAdminHelp: '分享给需要编辑问卷的协作者；对方需登录。',
    shareCopyAdmin: '复制管理链接',

    resultsScopeHint: "当前分析和导出包含 {shown} / {total} 份提交。CSV 每行一份提交；ZIP 包含逐轮长表和汇总。题目搜索只改变显示的卡片。",
    resultsBrowserPrefs: "筛选偏好仅保存在当前浏览器，不会修改问卷。",
    resultsResetFilters: "重置筛选",
    resultsFilteredEmpty: "没有符合当前筛选条件的提交。重置筛选可查看全部答卷。",
    resultsPreparingExport: "正在准备导出…",
    resultsViewResponse: "查看",
    resultsSubmissionDetail: "提交详情",
    resultsHistoricalRevision: "未记录（历史答卷）",
    resultsGroupExplanation: "一次回答评价整组展示媒体，每种有序媒体组合只计一次。",
    resultsCounts: "已回答 {answered} / {total} 份提交（{rate}%）· {people} 位参与者",
    resultsRecordTrial: "轮次",
    resultsNoMedia: "无媒体",
    resultsClose: "关闭",
    resultsRevision: "问卷版本",
    resultsQuality: "质量标记",
    resultsDetailSession: "会话",
    resultsMixedRevisions: "这些答卷涉及多个问卷版本。分析和导出仅使用当前所选版本，避免新旧设置混算。可在下方切换；未记录版本的历史答卷仍标为未知。",
    resultsAllRevisions: "全部版本",
    resultsRowsPerPage: "每页行数",
    resultsTitle: '结果分析',
    resultsDescriptionPrefix: '按题目分析项目答卷：',
    resultsRefresh: '刷新答卷',
    resultsExportCsv: '导出 CSV',
    resultsExportAll: '全部导出',
    resultsExportMethods: '导出方法说明',
    resultsViewOverview: '概览',
    resultsViewQuestions: '题目',
    resultsViewData: '数据',
    resultsAnalyze: '分析当前结果',
    resultsExplainQuestion: '解释这道题',
    resultsFilters: '筛选',
    resultsExportMenu: '导出',
    resultsExportReport: '本次分析报告',
    resultsExportSummary: '汇总统计',
    resultsExportRaw: '原始／逐轮数据',
    resultsExportBundle: '完整分析包',
    resultsParticipants: '独立参与者',
    resultsTrials: '回答轮次',
    resultsLatestReport: '最近分析',
    resultsNoReport: '还没有分析报告。点击“分析当前结果”生成。',
    resultsReportStaleScope: '这份报告来自之前的分析范围。请重新分析以更新。',
    resultsReportStaleData: '报告之后又有新答卷。请重新分析以更新。',
    resultsViewEvidence: '查看依据',
    resultsDeepPerception: '标注 × 感知 深入分析',
    resultsOpenPerception: '打开深入分析',
    resultsDataSource: '数据来源',
    resultsSourceHuman: '真人答卷',
    resultsSourcePractice: '仅研究者练习',
    resultsSourceSilicon: 'Silicon 运行',
    resultsSiliconRun: 'Silicon 运行 ID',
    resultsApplyFilters: '应用',
    resultsScopeNow: '当前范围',
    resultsSelectQuestion: '选择题目',
    resultsQuestionSearch: '搜索题目',
    resultsDateTimezone: '日期按 {tz} 计算。启用日期筛选时，没有有效时间戳的记录不纳入。',
    resultsQualityIsInfo: '质量标记只是检查信息，不会自动删除答卷。',
    resultsContractFallback: '部分历史答卷没有冻结题目合同，当前分析使用现有设置作为替代。',
    resultsFormalHuman: '正式分析默认使用真人数据；练习或 Silicon 需明确选择。',
    resultsConnectedSupabase: '已连接 Supabase',
    resultsLocalFiles: '读取本地答卷文件',
    resultsDataQuality: '数据质量',
    resultsClean: '{n} 条干净',
    resultsFlagged: '{n} 条标记',
    resultsInAnalysis: '{n} 条纳入分析',
    resultsExcludeFlagged: '分析时排除已标记答卷',
    resultsTotalResponses: '答卷总数',
    resultsAnswerableQuestions: '可答题数',
    resultsWithResponses: '有作答',
    resultsQuestionCoverage: '题目覆盖率',
    resultsCoverageHelp: '至少有 1 条作答的可答题',
    resultsResponseRecords: '答卷记录',
    resultsSubmissions: '{n} 份提交',
    resultsRecordsHelp: '删除本项目的单条提交。删除后不可恢复。',
    resultsRecordsViewHelp: '查看每份提交的答案、逐轮详情及质量标记。',
    resultsEmpty: '尚无答卷。分享问卷链接后，再回到这里查看结果。',
    resultsSkillCustomHelp:
      '自定义 Skill 结果按声明类型复用原生分析：标注类（points/path/polygon/bbox）叠加热力/图形；媒体评分类按图片排序；文字/排序/分配类用对应原生图表。',
    resultsSkillByMode: '按任务模式查看',
    resultsSkillByModeHelp:
      '此 Skill 把多种任务写在同一个题里，用 mode 区分（例如线索排序 / 路线描绘）。',
    resultsSkillFieldStats: '字段统计',
    resultsSkillResponseList: '回答一览',
    resultsSkillExampleFirst: '示例（第 1 条）',
    resultsSkillReadableSummary: '可读摘要',
    resultsSkillHideAllRaw: '隐藏全部原始回答',
    resultsSkillShowAllRaw: '查看全部原始回答',
    resultsSkillHideRawJson: '隐藏原始 JSON',
    resultsSkillShowRawJson: '显示原始 JSON',
    resultsSkillReadableHint: '每人一条可读摘要；需要时可展开原始 JSON。',
    resultsSkillRawHint: '仅显示答案字段；刺激媒体在每条下方单独列出。',
    resultsSkillResponseN: '回答 {n}',
    resultsSkillStimulusMedia: '刺激媒体：',
    resultsSkillShowLess: '收起',
    resultsSkillShowAllN: '查看全部 {n} 条',
    resultsSkillNoResponses: '尚无回答。',
    resultsSkillHideRaw: '隐藏原始回答',
    resultsSkillViewRaw: '查看原始回答',
    skillAnswerSubmittedTitle: '已提交的回答',
    skillAnswerYourSubmittedTitle: '你刚才提交的回答',
    skillAnswerRecorded: '已记录',
    skillAnswerEmpty: '（未作答）',
    skillAnswerNotRecorded: '此题尚未记录到答案（完成键未成功写入，或未作答）。',

    practiceTitle: '研究者练习',
    practiceDescription: '练习答题、验证媒体分配，并在邀请参与者前检查结果。',
    practiceQuestions: '题目',
    practiceStartSession: '开始练习会话',
    practiceSessionLocked: '会话已锁定 — 按队列作答，或结束会话后自由选题。',
    practiceFreePick: '点击任意题目自由作答。所选题目的结果分析在下方（默认折叠）。',
    practiceChooseQuestion: '请在左侧选择一道题开始练习。',
    practiceTotal: '练习总数：{n}',
    practiceNewRound: '新一轮',
    practiceEndSession: '结束会话',
    practiceEditSettings: '题目设置',
    practiceSettingsSaved: '题目设置已更新。请保存项目以持久化。',
    practiceSettingsUnavailable: '缺少问卷配置，无法编辑题目设置。',

    sidebarProjects: '项目',
    sidebarTemplates: '项目模板',
    sidebarSearchTemplates: '搜索模板…',
    sidebarAllCategories: '全部分类',
    sidebarCatAcademic: '学术研究',
    sidebarCatUrban: '城市理论',
    sidebarCatAi: 'AI 模板',
    sidebarSortNameAsc: '名称 A–Z',
    sidebarSortNameDesc: '名称 Z–A',
    sidebarSortNewest: '最新',
    sidebarSortOldest: '最早',
    sidebarPinned: '置顶',
    sidebarUnknownAuthor: '未知作者',
    sidebarShowMetadata: '显示元数据',
    sidebarHideMetadata: '隐藏元数据',
    sidebarPreviewTemplate: '预览模板',
    sidebarCopyTemplate: '复制模板',
    sidebarNoDescription: '暂无描述',
    sidebarMyProjects: '我的项目（{n}）',
    sidebarImportProject: '导入项目',
    sidebarCreateProject: '新建项目',
    sidebarNoProjects: '还没有项目。创建你的第一个项目吧！',
    sidebarNoTemplates: '暂无模板。可从项目另存为模板。',
    sidebarNoTemplateMatch: '没有匹配的模板。',
    sidebarProjectName: '项目名称',
    sidebarDescriptionOptional: '描述（可选）',
    sidebarCancel: '取消',
    sidebarCreate: '创建',
    sidebarEditProject: '编辑项目',
    sidebarDuplicate: '复制',
    sidebarExportProject: '导出项目',
    sidebarExportIde: '导出给 AI / IDE',
    sidebarExportIdeHelp: '已去除凭据',
    sidebarSaveAsTemplate: '另存为模板',
    sidebarPublishMain: '发布到主页',
    sidebarDelete: '删除',

    integBackAdmin: '返回 Admin',
    integTitle: 'AI 与集成',
    integBody:
      '优先使用站内助手和你自己的 API 密钥（推荐 DeepSeek）。Codex / Claude / Cursor 的 MCP 是高级选项。',
    integConnectTitle: '高级 — 连接 ChatGPT（Codex）',
    integAdvanced: '高级',
    integAssistantFirstBody: '填入各提供方的 API 密钥即可使用其模型。保存后密钥只写不回读，交互与 DeepSeek Harness 一致。',
    integAssistantModel: '助手模型',
    integSiliconModel: '硅基 VLM',
    integRecommended: '推荐',
    integConnected: '已连接',
    integConnectBody:
      '用 ChatGPT（Codex）创建与编辑问卷。保存后分享链接立即更新。本站无需填写 OpenAI API 密钥。',
    integMcpEndpoint: 'MCP 地址（Codex / Claude Code / Cursor 共用）：',
    integLocalMcpHint: '本地开发 MCP（仅调试用）',
    integStep1Title: '将 ChatGPT（Codex）权限设为 “Approve for me”',
    integStep1Body:
      '在 ChatGPT（Codex）Work 输入区，点击左下角权限控件，选择 Approve for me。这样 ChatGPT（Codex）才能编辑 ~/.codex/config.toml、运行 codex mcp login、打开浏览器并保存凭证。',
    integStep2Title: '粘贴到 ChatGPT（Codex）',
    integStep2Body: '复制到一个新的 ChatGPT（Codex）对话：',
    integStep3Title: '开新对话，然后设计问卷',
    integStep3Body: '打开一个新的 ChatGPT（Codex）对话，然后粘贴：',
    integClaudeTitle: '2. 连接 Claude Code',
    integClaudeBody:
      '在终端用 Claude Code 连接同一 sp_survey MCP。保存后分享链接立即更新。首次需在浏览器完成 OAuth 授权。',
    integClaudeStep1Title: '添加 MCP 服务器',
    integClaudeStep1Body:
      '在终端执行下面命令（或把提示词粘贴给 Claude Code）。建议用 user scope，这样所有项目都能用：',
    integOrPastePrompt: '或把下面设置提示词粘贴给 agent：',
    integClaudeStep2Title: '在 Claude Code 中授权',
    integClaudeStep2Body:
      '运行 /mcp，选择 sp_survey，再选 Authenticate。在系统浏览器打开授权页并在本站 Approve。若工具未出现，重启 Claude Code。',
    integClaudeStep3Title: '设计问卷',
    integClaudeStep3Body: '在新的 Claude Code 会话中粘贴：',
    integCursorTitle: '3. 连接 Cursor',
    integCursorBody:
      '在 Cursor Agent 中使用同一 sp_survey MCP。写入 JSON 配置后，到 Settings → Tools & MCP 完成 Connect / Authenticate。',
    integCursorStep1Title: '写入 ~/.cursor/mcp.json（或项目 .cursor/mcp.json）',
    integCursorStep1Body:
      '合并进已有 mcpServers，不要清空其他服务器。Cursor 只需 url（不要加 type）：',
    integCursorStep2Title: '在 Cursor 中授权',
    integCursorStep2Body:
      '打开 Cursor Settings → Tools & MCP，找到 sp_survey，点击 Connect / Authenticate。在浏览器打开本站并 Approve。若工具仍为灰色，Reload Window。',
    integCursorStep3Title: '设计问卷',
    integCursorStep3Body: '在新的 Cursor Agent 对话中粘贴：',
    integAccounts: '已连接的 MCP 账号',
    integNoneYet: '尚无连接。请先完成上面 Codex、Claude Code 或 Cursor 的连接步骤。',
    integLastUsed: '最近使用：',
    integNever: '从未',
    integDisconnect: '断开连接',
    integAssistantTitle: '1. 站内 AI 助手',
    integReady: '已就绪',
    integAssistantBody: '想留在浏览器里？添加 OpenAI 或 OpenRouter API 密钥后，可在问卷构建中使用助手。',
    integGetKey: '从 OpenAI 或 OpenRouter 获取密钥。',
    integApiKeyLabel: 'OpenAI 或 OpenRouter API 密钥',
    integSaveKey: '保存密钥',
    integRemoveKey: '移除密钥',
    integAfterSave: '保存后返回 Admin，打开项目，并展开 AI 助手面板。',
    integCopy: '复制',
    modelsTitle: '模型',
    modelsIntro: '填入各提供方的 API 密钥即可使用其模型。',
    modelsEdit: '编辑',
    modelsAdd: '添加提供方',
    modelsProvider: '提供方',
    modelsCancel: '取消',
    modelsApply: '保存',
    modelsApplying: '保存中…',
    modelsSavedProvider: '已保存 {provider}。',
    modelsCredentialConfigured: 'API 密钥已配置',
    modelsCredentialMissing: 'API 密钥缺失',
    modelsLoadFailed: '加载提供方目录失败',
    modelsReload: '重试',
    modelsKeyInput: 'API 密钥',
    modelsKeyPlaceholder: '输入 API 密钥',
    modelsKeyStored: '已配置——输入新值可替换',
    modelsCustomized: '自定义设置',
    modelsBaseUrl: 'API 地址',
    modelsBaseUrlDefault: '提供方默认',
    modelsModels: '模型目录',
    modelsSearchModels: '搜索模型',
    modelsSearchModelsHint: '按模型名称或 ID 搜索',
    modelsNoMatchingModels: '没有匹配的模型。',
    modelsAddModel: '添加模型',
    modelsRemoveModel: '删除模型',
    modelsModelId: '模型 ID',
    modelsModelName: '显示名称',
    modelsModelNamePlaceholder: '留空时使用模型 ID',
    modelsFetchModels: '获取可用模型',
    modelsFetching: '正在询问提供方…',
    modelsFetchNeedsBaseUrl: '请先填写 API 地址，再获取。',
    modelsFetchEmpty: '该提供方没有列出任何模型，请手动添加。',
    modelsFetchTitle: '选择要添加的模型',
    modelsFetchDescription: '以下是模型提供方的可用模型，勾选要添加的模型。',
    modelsFetchSelectAll: '全选',
    modelsFetchDeselectAll: '取消全选',
    modelsFetchAdopt: '添加所选',
    modelsCustomAdd: '添加自定义提供方',
    modelsCustomTitle: '自定义提供方',
    modelsCustomTag: '自定义',
    modelsCustomRoute: 'Provider ID',
    modelsCustomRouteHint: '以小写字母开头的标识，在请求中唯一标识该提供方，并用于派生凭据名。',
    modelsCustomRouteInvalid: '需以小写字母开头，之后可用小写字母、数字和短横线。',
    modelsCustomRouteTaken: '已有提供方使用了这个 ID。',
    modelsCustomDisplayName: '显示名称',
    modelsCustomApi: 'API 协议',
    modelsCustomNeedsBaseUrl: '自定义提供方需要填写 API 地址。',
    modelsCustomNeedsModels: '自定义提供方至少需要一个模型。',
    modelsCustomBaseUrlPlaceholder: 'https://gateway.example/v1',
    modelsCreate: '创建提供方',
    modelsCreating: '创建中…',
    modelsOnboardingTitle: '添加一个 API Key 开始使用',
    modelsKeyRequired: '请输入 API 密钥后继续。',
    modelsSelectModel: '选择模型',
    modelsSelectVision: '选择视觉模型',
    modelsDefaults: '默认模型',
    modelsDeleteDescriptionWithCredential: '删除 {provider} 会移除其配置和存储的 API 密钥。',
    modelsAuthUnsupported: '本轮不支持配置原生认证',
    modelsGroupRecommended: '推荐',
    modelsGroupConfigured: '已配置',
    modelsGroupCatalog: '目录',
    modelsGroupCustom: '自定义',
    modelsGroupUnsupported: '原生认证（暂不可配置）',
    modelsContextWindow: '上下文窗口',
    modelsMaxOutput: '最大输出',
    modelsInputText: '文本',
    modelsInputImage: '图像',
    modelsReasoning: '推理',
    modelsReasoningOff: '关闭',
    modelsDefaultEffort: '默认推理级别',
    modelsRetry: '最大重试次数',
    modelsSettingsConflict: '设置已被他人更改。请关闭后重新打开以编辑当前值。',
    modelsDiscovery401: '获取可用模型返回 401。',
    modelsDiscoveryUnsupported: '该协议不支持模型发现。',
    modelsDiscoveryTooLarge: '提供方响应超过 4 MiB。',
    modelsDiscoveryMalformed: '提供方返回了无法解析的 JSON。',
    modelsDiscoveryNetwork: '无法连接提供方。请检查地址后重试。',
    siliconOverrideModel: '本次运行的视觉模型',
    siliconEffort: '推理级别',
    siliconDisclaimer: '硅基样本仅用于预试与假设探索，不会计入真人配额，也不会进入默认结果分析，更不能证明设计已经科学有效。',
    siliconPersonas: '人设',
    siliconName: '名称',
    siliconCity: '城市 / 国家',
    siliconNotes: '情景备注',
    siliconAddPersona: '添加人设',
    siliconRemove: '删除',
    siliconRun: '运行',
    siliconRepeats: '重复次数',
    siliconBudget: 'Token 预算',
    siliconDraftSnapshot: '每次运行都会固化当前已保存草稿及其项目媒体。',
    siliconBudgetUsage: '已用 {used} / {budget} tokens',
    siliconStart: '运行硅基预试',
    siliconNeedVlm: '使用你配置的硅基 VLM（必须能看图）。请在「AI 与集成」中设置。',
    siliconRuns: '运行记录',
    siliconCancel: '取消',
    siliconCompare: '对照',
    siliconCount: '合成答卷',
    siliconAnswers: '条答案',
    siliconPickPersona: '请至少选择一个人设。',
    siliconResume: '继续未完成任务',
    siliconRetryFailed: '仅重试失败项',
    siliconResumeHint: '保留已保存答案，只处理未完成的轮次。',
    siliconRetryHint: '只把本任务中失败或未知的轮次重新排队，不会新开一整批。',
    siliconReuseHint: '用相同设置创建新任务。旧任务的答案不会被覆盖。',
    siliconUnverifiedQs: '已列出但尚未预发布验收',
    siliconErrorLease: '当前数据库尚未应用硅基后台租约。',
    draftSaveRpcMissing: '当前数据库尚未应用草稿保存函数。请执行 save_project_draft_revision_id.sql。',
    assistantRunSchemaMissing: '当前数据库尚未应用助手 run 列。',
    siliconExport: '导出 JSON',
    siliconExportCsv: '导出 CSV',
    siliconDraftVersion: '草稿版本',
    siliconModelLabel: '模型',
    siliconSelectedPersonas: '已选人设',
    siliconSupportedQs: '硅基可以作答',
    siliconUnsupportedQs: '硅基将跳过',
    siliconNoAnswerable: '已保存草稿中没有可作答题。',
    siliconSelectQuestions: '开始前请至少选择一道受支持题目。',
    siliconSnapshotPages: '快照页数',
    siliconSnapshotMedia: '快照媒体',
    siliconCannotStart: '没有可测的受支持题目。未测项已列出，局部成功不能写成整卷通过。',
    siliconPartialPretest: '这是仅覆盖受支持题目的局部预试。未测项已列出，局部成功不能写成整卷通过。',
    siliconOpenPersona: '打开人设全文',
    siliconValidRate: '有效率',
    siliconFailureReason: '失败原因',
    siliconShownMedia: '本次展示媒体',
    siliconStatusAllFailed: '已结束，未获得有效答案',
    siliconStatusPartialValid: '已结束 — 部分答案无效或跳过',
    siliconProgressCounts: '已处理 {done}/{total} 轮 · 有效 {answered} · 跳过 {skipped} · 失败 {errors} · 剩余 {remaining}',
    siliconStatusAllComplete: '已结束，已获得有效答案',
    aiChangeCardTitle: '修改结果',
    aiChangeVerified: '已保存并核对',
    aiChangeSaved: '已保存',
    aiChangeUnsaved: '未保存',
    aiChangeSaveFailed: '保存失败',
    aiChangeVerifyIncomplete: '核对未完成',
    aiChangeNoDiff: '没有可展示的字段变化',
    aiWriteConflict: '编辑器有未保存修改。先保存再执行本条，或返回设置。',
    aiSaveThenRun: '保存后执行',
    aiReturnToEditor: '返回设置',
    aiQueueTitle: '任务队列',
    aiQueueAfter: '完成后执行',
    aiQueueNow: '补充当前',
    aiQueueCancel: '取消',
    siliconStatusPartial: '部分完成 — 预算中止',
    siliconStatusBudgetFailed: '已停止 — Token 预算耗尽',
    siliconStatusFailed: '失败',
    siliconStatusCancelled: '已取消',
    siliconStatusRunning: '进行中',
    siliconStatusLoading: '正在加载统计…',
    siliconTasksTitle: '硅基任务',
    siliconTasksTab: '运行任务 · {count}',
    siliconTasksBadge: '任务 · {count} 运行中',
    siliconTasksActive: '{count} 个进行中的硅基任务',
    siliconTasksRecent: '已结束',
    siliconTasksEmpty: '还没有硅基任务。',
    siliconTasksEmptyActive: '没有进行中的硅基任务。',
    siliconTasksEmptyEnded: '还没有已结束的硅基任务。',
    siliconTasksInProgress: '进行中（{count}）',
    siliconTasksEnded: '已结束（{count}）',
    siliconTasksBack: '返回',
    siliconProcessTitle: '运行过程',
    siliconProcessSamples: '已处理 {done}/{total} 轮',
    siliconProcessUnits: '已处理 {processed}/{total} 轮：有效 {valid}，跳过 {skipped}，失败 {failed}',
    siliconProcessCurrent: '当前：{persona} · 第 {repeat} 份答卷 · {question} · 第 {trial}/{trials} 轮',
    siliconProcessWaiting: '正在等待模型返回 · 本步已等待 {seconds} 秒',
    siliconProcessTimeline: '技术详情',
    siliconConfigSummary: '{personas} 个人设 · {envelopes} 份模拟答卷 · {questions} 道题，共 {trials} 轮',
    siliconProcessedTrials: '已处理 {done}/{total} 轮',
    siliconEndedNoAnswers: '已结束，未获得有效答案',
    siliconTrialBreakdown: '已处理 {done}/{total} 轮：有效 {valid}，跳过 {skipped}',
    siliconNoImagesReason: '原因：未解析到可用图片',
    siliconReuseSettings: '复用设置重新运行',
    siliconMediaPreviewLibrary: '本次使用平台预览媒体库 · 可用 {count} 张图片',
    siliconMediaProject: '本次使用项目媒体 · 可用 {count} 张图片',
    siliconMediaNone: '本次没有可用媒体来源',
    siliconEnvelopeLabel: '{persona} · 第 {repeat} 份答卷',
    siliconQuestionCard: '第 {n} 题｜{title}',
    siliconTrialProgress: '已处理 {done}/{total} 轮 · 正在进行第 {current} 轮',
    siliconWaitingStart: '等待开始',
    siliconTechDetails: '技术详情',
    siliconEndedAt: '结束于 {time}',
    siliconDuration: '运行 {duration}',
    siliconDurationSeconds: '{seconds} 秒',
    siliconDurationMinutes: '{minutes} 分 {seconds} 秒',
    siliconStatusQueued: '排队中',
    siliconStatusThrottled: '等待限流',
    siliconStatusStopping: '正在停止',
    siliconErrorNoMedia: '没有可用媒体来源。项目媒体为空，预览媒体库也没有图片。',
    siliconErrorFolderEmpty: '指定文件夹没有匹配图片。',
    siliconErrorImageUnreadable: '图片链接无法读取。',
    siliconErrorModel: '模型请求失败。',
    siliconViewProcess: '查看过程',
    siliconViewAnswers: '查看已完成答卷',
    siliconOpenProject: '打开所属项目',
    siliconStopping: '正在停止…',
    siliconConnectionLost: '连接中断。任务仍在服务端推进，这不是任务失败。',
    siliconTaskFinished: '{project} 的硅基任务已{status}。',
    siliconTaskCounts: '已处理 {processed}/{total} 轮 · 有效 {valid} · 失败 {failed}',
    siliconRationale: '作答依据',
    siliconEventStart: '已开始本题',
    siliconEventMedia: '已准备本轮图片',
    siliconEventRequest: '正在请求模型',
    siliconEventResponse: '已收到答案',
    siliconEventValid: '答案校验通过',
    siliconEventInvalid: '答案校验失败',
    siliconEventSkip: '已跳过',
    siliconEventSaved: '答案已保存',
    siliconEventRetry: '重试中',
    siliconEventThrottle: '排队或限流中',
    siliconEventStopping: '正在停止',
    siliconEventComplete: '任务完成',
    siliconEventFailed: '任务失败',
    siliconStageQueued: '排队中',
    siliconScienceNote: '这些数字只是合成预试，不能当作问卷已科学有效的证据。',
    featureAssistant: '浏览器内助手',
    featureAssistantHint: '与硅基预试独立。关闭后隐藏助手侧栏。',
    featureSilicon: '硅基预试（实验）',
    featureSiliconHint: '与助手独立。未达标前保持实验可见范围。',
    qualityChecks: '运行设计检查',
    qualityChecksEmpty: '未发现可立即处理的设计问题。',
    qualityCheckResearch: '研究设计',
    qualityCheckFlow: '参与者流程',
    qualityCheckExport: '分析 / 导出',
    resultsSiliconHint: '默认只看真人结果。硅基对照请打开「硅基样本」标签。',

    navPaperLibrary: '论文库',
    navNews: '新闻',
    navRequestTemplate: '申请模板',
    navRequestDesign: '申请设计',
    navTeam: '团队',
    navLiveSurveys: '在线调查',
    navSpBench: 'SP-Bench',
    navResearcherLogin: '研究者登录',
    footerDevelopedBy: '开发团队',

    newsTitle: '新闻',
    newsSubtitle: 'SP-Survey 的产品更新、发布说明与研究动态。',
    newsEmpty: '暂无内容，稍后再来看看。',
    newsBackToList: '全部新闻',
    newsNotFound: '未找到该文章，或尚未发布。',
    newsNoBody: '暂无正文。',
    newsUnavailable: '新闻功能尚未就绪。',

    benchNotOpenTitle: 'SP-Bench',
    benchNotOpenBody: '公开评测榜暂未开放。',
    benchMethod: '方法',
    benchDataset: '数据集',
    benchSamples: '样本',
    benchModels: '模型',
    benchOverall: '总榜',
    benchObjective: '客观感知',
    benchSubjective: '主观感知',
    benchCognition: '空间认知',
    benchMethodTab: '方法说明',
    benchLandingFallback: '面向城市环境主观—客观空间感知与认知的多模态模型基准评测。',
    benchNoResultsYet: '榜单即将发布',
    benchViewLeaderboard: '查看排行榜',

    landLiveBannerOne: '当前有 {n} 份调查正在进行 — 可作为参与者填写。',
    landLiveBannerMany: '当前有 {n} 份调查正在进行 — 可作为参与者填写。',
    landBrowseLive: '浏览在线调查',
    landHeroTitle: '街景感知调查',
    landHeroLine1: '面向城市街景视觉感知研究的专业级调查平台。',
    landHeroLine2: '构建、分享并分析街景感知研究 — 无需编程。',
    landStartFree: '免费开始 →',
    landReadPaper: '阅读论文',
    landFeaturesTitle: '感知研究所需的一切',
    landFeatUploadTitle: '上传图片',
    landFeatUploadDesc: '将街景图片直接上传到云存储，自动压缩以保持问卷加载迅速。',
    landFeatAiTitle: 'AI 问卷构建',
    landFeatAiDesc: '描述研究目标，由 ChatGPT 生成完整问卷，并使用经验证的题型。',
    landFeatShareTitle: '即时分享',
    landFeatShareDesc: '立即获得可分享链接 — 无需服务器、无需部署、无需复杂配置。',
    landFeatResultsTitle: '结果分析',
    landFeatResultsDesc: '按题目查看答卷，支持图片与回答配对，并可导出 CSV。',
    landTemplatesTitle: '模板库',
    landTemplatesSubtitle: '从已发表研究中使用的同行评议问卷设计起步',
    landRequestTemplate: '为你的论文申请模板',
    landRequestDesign: '申请问卷设计协助',
    landSearchTemplates: '按名称、作者或主题搜索模板…',
    landCatAcademic: '学术研究',
    landCatUrban: '城市理论',
    landCatAi: 'AI 生成',
    landSearchCategoryHint: '搜索以浏览该类别全部 {n} 个模板。',
    landNoTemplates: '未找到与 “{q}” 匹配的模板',
    landCtaTitle: '准备好开始你的调查了吗？',
    landCtaBody: '创建账号，10 分钟内即可上线第一份问卷。',
    landGetStarted: '免费开始',
    landPreview: '预览',
    landUse: '使用',
    landPaper: '论文',
    landDataset: '数据集',
    landChipAi: 'AI',
    landChipUrban: '城市',
    landChipAcademic: '学术',
    landPreviewTitle: '预览 — {name}',
    landReadOnly: '（只读）',
    landClose: '关闭',

    trialDoNTimes: '本题共需完成 {n} 轮。当前第 {current} / {n} 轮。',
    trialProgressCount: '已完成 {done} / {n}，剩余 {left} 轮。',
    trialAutoAdvance: '选择后会自动进入下一轮。',
    trialUseNext: '答完后请点「下一轮」。',
    trialLastRound: '最后一轮 — 完成后即可继续问卷。',
    trialAllDone: '已完成全部 {n} 轮。',
    trialContinue: '继续下一题',
    trialFinishSurvey: '完成问卷',
    trialBack: '返回',
    trialNextRound: '下一轮',
    trialPageNextBlocked: '请先完成全部轮次再继续。',
    progressRound: '第 {current} / {n} 轮',
    trialLabel: '第 {n} 轮',
    trialReviewAnswers: '检查答案',
    progressPage: '第 {current} / {n} 页',
    progressQuestion: '第 {current} / {n} 题',
    progressQuestionLabel: '第 {n} 题',
    progressTrialCount: '已完成 {done}/{n} 轮',
    progressNotReached: '尚未到达',
    progressJump: '{label} — 点击跳转',
    progressPreviousGroup: '上一组题目',
    progressNextGroup: '下一组题目',
    participantSaving: '正在保存您的答案…',
    participantThanks: '感谢参与！',
    participantCompleted: '感谢您完成问卷！',
    participantRoundsCompleted: '已完成全部 {n} 轮标注！',
    participantCompletionCode: '完成码：',
    participantIdLabel: '您的参与者编号：',
    participantSavedLocally: '答案已保存在本地。',
    participantSaved: '答案已成功保存。',
    participantSubmitted: '答卷已提交。',
    participantSaveError: '由于网络或服务器问题，答案暂时未能保存。您的答案已保留，请重试提交。',
    participantRetrySubmission: '重试提交',
    participantResumeTitle: '继续上次的问卷？',
    participantResumeBody: '发现您在 {date} 保存的未完成问卷。是否从上次的位置继续？',
    participantPreviousVisit: '上次访问时',
    participantStartFresh: '重新开始',
    participantResume: '继续填写',

    loginBrand: '街景感知调查',
    loginSubtitle: '研究者登录',
    loginSignIn: '登录',
    loginCreateAccount: '创建账号',
    loginEmail: '邮箱',
    loginPassword: '密码',
    loginPasswordHelp: '至少 6 个字符',
    loginSigningIn: '登录中…',
    loginCreating: '创建账号中…',
    loginRegisterSuccess: '注册成功！请查收邮件完成确认后再登录。',
    loginErrorGeneric: '发生错误',
    loginBackHome: '← 返回首页',

    liveTitle: '在线调查',
    liveBody: '平台当前展示的社区研究。参与者链接遵循研究者设置的正式版本。灰色卡片表示不在批准的时间窗口内。',
    liveEmpty: '暂无展示中的在线调查。研究者可在项目菜单中申请「发布到主页」；管理员在后台审核后展示。',
    liveNowOnline: '正在进行（{n}）',
    liveUpcomingClosed: '即将开始与已结束',
    liveOnline: '进行中',
    liveUpcoming: '即将开始',
    liveClosed: '已结束',
    liveNoDescription: '暂无描述。',
    liveTakeSurvey: '填写问卷',
    liveNotOpenYet: '尚未开放',

    teamTitle: '团队',
    teamIntroBefore: 'SP-Survey 由',
    teamIntroMid: '在',
    teamIntroAfter: '（新加坡国立大学）主导，与合作机构共同推进，并由 Filip Biljecki 指导。',
    teamCollaborators: '合作者',
    teamAffiliations: '单位',
    teamProfile: '主页',
    teamProjectLead: '项目负责人',
    teamPI: '首席研究员',
    teamLeadBlurb: '发起并主导 SP-Survey 的设计、开发与开源发布——从平台架构、问卷工具到托管研究流程。',
    teamPiBlurb: 'Urban Analytics Lab 创始人；为 SP-Survey 提供学术指导与研究方向。',
    teamRolePhd: '博士研究生',
    teamRoleEngineer: '研究工程师',
    teamRoleFellow: '研究员',
    teamRoleVisiting: '访问学者',
    teamRoleAssocProf: '副教授',

    papersTitle: '论文库',
    papersCount: '{n} 篇论文',
    papersWithTemplate: '{n} 篇有模板',
    papersWithTemplateTitle: '仅显示已关联问卷模板的论文',
    papersBody: '精选的城市影像 × 人工调查文献，用于支撑本平台的问卷模板。管理员将论文匹配到现有模板后，会显示关联模板 ID。',
    papersAll: '全部论文',
    papersHasTemplate: '有模板（{n}）',
    papersNoTemplate: '无模板（{n}）',
    papersSearchPlaceholder: '标题 / DOI / 期刊 / 作者 / 模板 ID',
    papersSearch: '搜索',
    papersClearSearch: '清除搜索',
    papersShowing: '显示 {shown} / {total} 篇',
    papersHasTemplateSuffix: ' · 有模板',
    papersNoTemplateSuffix: ' · 无模板',
    papersNoMatch: '没有匹配的论文。',
    papersOpen: '打开论文',
    papersColTitle: '标题',
    papersColYear: '年份',
    papersColTemplate: '模板',
    papersColLink: '链接',

    reqCopied: '已复制',
    reqCopy: '复制',
    reqSubmitAnother: '再提交一份',
    reqBackHome: '返回首页',
    reqStarting: '开始中…',
    reqUploading: '上传中（{current}/{total}）…',
    reqSubmitting: '提交中…',
    reqSubmit: '提交申请',
    reqChooseFiles: '选择文件',
    reqClear: '清除',
    reqFilesSelected: '已选 {n} 个文件',
    reqMore: '另有 {n} 个',
    reqMax: '最多 {n}',
    reqUploaded: ' 已上传。',
    reqFileTooLarge: '“{name}”过大（最大 {mb} MB）。',
    reqUploadUnavailable: '当前无法上传文件。请先不带文件提交，或稍后再试。',

    reqTplTitle: '为你的论文申请模板',
    reqTplIntro: '无需账号。提供论文信息、可选的调查数据集图片与补充文件（PDF 等）。我们会生成待审核的模板 ID，审核通过后即可使用。需要协助设计定制研究？',
    reqTplLinkDesign: '申请问卷设计',
    reqTplSuccess: '申请已提交。请保存你的待审核模板 ID。',
    reqTplId: '模板 ID',
    reqTplImagesCount: '{n} 张调查数据集图片',
    reqTplSuppCount: '{n} 个补充文件',
    reqTplStatus: '状态：待审核（尚未进入公共库）。通过后，打开研究者面板并使用此模板 ID。',
    reqTplNeedFields: '请填写论文标题、作者与论文链接。',
    reqTplBadYear: '年份须为四位数字（如 2025）。',
    reqTplBadEmail: '请输入有效邮箱，或留空。',
    reqTplPaperTitle: '论文标题',
    reqTplAuthors: '作者',
    reqTplAuthorsHelp: '例如 Yang, Sijie and Chong, Adrian and Liu, Pengyuan and Biljecki, Filip',
    reqTplYear: '年份',
    reqTplYearPh: '如 2025',
    reqTplLink: '论文链接',
    reqTplLinkPh: 'https://doi.org/… 或出版商链接',
    reqTplEmail: '邮箱（可选）',
    reqTplEmailHelp: '可选 — 便于我们就该申请联系你。无需账号。',
    reqTplNotes: '备注（可选）',
    reqTplNotesHelp: '方法细节、问卷设计提示、Hugging Face 数据集等。',
    reqTplImages: '调查数据集图片（可选）',
    reqTplImagesHelp: '上传前会自动压缩图片。',
    reqTplChooseImages: '选择图片',
    reqTplSupp: '补充文件（可选）',
    reqTplSuppHelp: 'PDF、Word、幻灯片、表格、ZIP 等 — 每个最大 {mb} MB。',

    reqDesTitle: '申请问卷设计',
    reqDesIntro: '计划做一项感知研究，希望协助做成 SP-Survey？请提交简短研究说明 — 这是尽力而为的研究协作，并非付费服务。若要把已发表论文做成可复用模板？',
    reqDesLinkTpl: '改为申请模板',
    reqDesSuccess: '申请已提交。请保存申请 ID — 我们有能力协助时会通过邮件联系你。',
    reqDesId: '申请 ID',
    reqDesMediaCount: '{n} 个媒体文件',
    reqDesSuppCount: '{n} 个补充文件',
    reqDesStatus: '状态：待处理。我们按容量审核申请，若能协助设计或搭建问卷会发邮件通知。',
    reqDesStartSelf: '或自己开始构建',
    reqDesNeedFields: '请填写姓名、邮箱、研究标题与研究说明。',
    reqDesBadEmail: '请输入有效邮箱，以便我们跟进。',
    reqDesBriefShort: '请更详细描述你的研究（至少几句话）。',
    reqDesName: '你的姓名',
    reqDesEmail: '邮箱',
    reqDesEmailHelp: '必填，便于我们跟进。',
    reqDesAffiliation: '单位（可选）',
    reqDesAffiliationPh: '如 Urban Analytics Lab, NUS',
    reqDesStudyTitle: '研究标题',
    reqDesBrief: '研究说明',
    reqDesBriefHelp: '你想测量什么、对象是谁、大致如何进行？几句话即可。',
    reqDesStimulus: '刺激材料（可选）',
    reqDesStimulusHelp: '参与者将看到或听到的内容。',
    reqDesStimImage: '图片',
    reqDesStimVideo: '视频',
    reqDesStimAudio: '音频',
    reqDesStimMixed: '混合媒体',
    reqDesStimOther: '其他 / 尚未确定',
    reqDesTimeline: '时间安排（可选）',
    reqDesTimelinePh: '如 8 月预实验，10 月正式收集',
    reqDesRelated: '相关链接（可选）',
    reqDesRelatedPh: '论文、项目页或数据集 URL',
    reqDesNotes: '补充说明（可选）',
    reqDesNotesHelp: '设想的题型、样本量、约束条件等。',
    reqDesSampleMedia: '样例媒体（可选）',
    reqDesSampleHelp: '图片、视频或音频示例。图片上传前会自动压缩。',
    reqDesChooseMedia: '选择媒体',
    reqDesSupp: '补充文件（可选）',
    reqDesSuppHelp: '方案说明、PDF、幻灯片、表格、ZIP 等 — 每个最大 {mb} MB。',
    reqDesInfo: 'SP-Survey 团队在能力范围内尽力支持。你也可以随时注册账号自行搭建问卷。',

    regionLabel: '区域',
    globalMode: '全球',
    chinaMode: '中国',
    languageToggle: 'English',
    imageDataset: '媒体数据集',
    datasetProvider: '数据集平台',
    huggingface: 'Hugging Face',
    modelscope: 'ModelScope（魔搭）',
    msToken: 'ModelScope 访问令牌（可选）',
    hfDatasetName: 'HuggingFace 数据集名称',
    msDatasetName: 'ModelScope 数据集名称',
    msPlaceholder: '如 sijiey/Thermal-Affordance-Dataset',
    hfTokenHelp: '可选：仅私有数据集需要。前往 huggingface.co/settings/tokens 获取',
    msTokenHelp: '可选：仅私有数据集需要。前往 modelscope.cn/my/myaccesstoken 获取',
    hfBrowse: '浏览 HuggingFace 数据集',
    msBrowse: '浏览 ModelScope 数据集',
    deploymentGuide: '部署指引',
    globalDeployment: 'Vercel 部署',
    chinaDeployment: 'Zeabur 部署（中国可用）',
    storageProvider: '图片存储平台',
    supabase: 'Supabase 存储',
    aliyunOss: '阿里云 OSS',
    chinaModeBanner: '中国区模式已启用 — 使用 ModelScope、阿里云 OSS 与 Zeabur',
  },
};

/** Replace `{n}` / `{name}` placeholders in a template string. */
export function tf(template, vars = {}) {
  return String(template || '').replace(/\{(\w+)\}/g, (_, key) =>
    vars[key] != null ? String(vars[key]) : `{${key}}`
  );
}
